import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../lib/api';
import PeerBenchmarkTrend from '../components/common/PeerBenchmarkTrend';
import logger from '../lib/logger';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageSubNav } from '../components/layout/PageSubNav';

const MATURITY_SUBNAV = [
  { id: 'overview', label: 'Overview', to: '/maturity-health', end: true },
  { id: 'deep-dives', label: 'Deep Dives', to: '/maturity-health/deep-dives' },
  { id: 'insights', label: 'Insights', to: '/maturity-health/insights' },
  { id: 'roadmap', label: 'Roadmap', to: '/maturity-health/roadmap' },
  { id: 'benchmark', label: 'Benchmark', to: '/maturity-health/benchmark' },
  { id: 'analysis', label: 'Analysis', to: '/maturity-health/analysis' },
  { id: 'financial', label: 'Financial', to: '/maturity-health/financial' },
  { id: 'history', label: 'History', to: '/maturity-health/history' },
  { id: 'report-card', label: 'Report Card', to: '/maturity-health/report-card' },
];
const MATURITY_TAB_SEGS = ['insights', 'roadmap', 'benchmark', 'analysis', 'financial', 'history', 'report-card'];
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { ActionImpact, ActionOwner } from '../components/maturity/RoadmapAction';
import { HeroAccents, healthHeroTheme } from '../components/layout/HeroAccents';
import PageToolbar from '../components/layout/PageToolbar';
import { getSectionTheme, getLightSectionTheme } from '../lib/sectionThemes';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend,
  LineChart, Line, ReferenceLine, ReferenceArea
} from 'recharts';
import {
  Activity, AlertTriangle, TrendingUp, Brain, Loader2, Shield, Settings2,
  LineChart as LineChartIcon, Target, ChevronDown, ChevronUp, Lightbulb,
  Map, BarChart3, DollarSign, CheckCircle2, XCircle, Clock, Zap, Eye, Link2,
  ClipboardCheck, History, User, FileText, Download, FileSpreadsheet, Presentation, Gauge,
  ChevronRight, ChevronLeft, ArrowRight, Minus, Plus, RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { MaturityAssessmentOverlay } from '../components/admin/MaturityAssessmentOverlay';
import MaturityActionPlanCard from '../components/maturity/MaturityActionPlanCard';
import { WeakestMeasureTile } from '../components/health/HealthActionSpine';
import { TileInfoButton } from '../components/common/TileInfoButton';
import { useQuarterDelta } from '../hooks/useQuarterDelta';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useDeepLinkOrg } from '../hooks/useDeepLinkOrg';


// Single source of truth for the "what is this view, why does it
// matter, what should I do" copy on each Maturity Health tab + the
// trend chart. Used by `<TileInfoButton>` popovers throughout this
// page so the explanations stay consistent and easy to update in
// one place. Keep each `body` to ~3 short sentences max — the
// TileInfoButton popover is 320px wide.
const TAB_INFO = {
  weighted_lei: {
    title: 'Maturity Health Score',
    body: 'Your overall Learning Maturity Health score (0-100), computed as a weighted blend of the five maturity pillars: Learning Demand & Productization (25%), Skills & Capability Alignment (20%), Governance & Operational Efficiency (20%), Insight & Value Demonstration (20%), and Adoption & Behavioral Impact (15%). The badge below shows your maturity band — Emerging, Developing, Managed, or Optimized. What to do: read this as the *upper bound* of ROI conversion possible at your current maturity level.',
  },
  primary_constraint: {
    title: 'Primary Constraint',
    body: 'The single weakest pillar, which caps your overall maturity (your maturity equals the weakest critical link, not the average). Score and "ROI Risk" estimate how much value is leaking because of this gap. What to do: this is the highest-leverage place to invest your next quarter — fixing it shifts the whole score, while improving stronger pillars barely moves the needle.',
  },
  uplift_potential: {
    title: 'Uplift Potential',
    body: "The estimated ROI improvement if you advance to the next maturity band. Calculated against your current value-chain projections using the maturity-factor multiplier. What to do: use this number with finance leaders to size the business case for governance + transfer-validation work — it's the dollar value of closing your current band gap.",
  },
  trend: {
    title: 'Maturity Trend',
    body: 'Score evolution across every assessment your org has run. The shaded bands mark the four maturity levels (Emerging → Developing → Managed → Optimized). What to do: look for plateaus or regressions — both signal that recent improvement actions stopped compounding and a fresh constraint needs attention.',
  },
  insights: {
    title: 'Insights tab',
    body: "Headline diagnosis of where your maturity stands today and what's holding it back. Surfaces your primary constraint (the weakest pillar, which caps overall maturity) plus the symptoms typical of your current band. What to do: treat the primary constraint as the highest-leverage place to invest your next quarter of effort.",
  },
  roadmap: {
    title: 'Roadmap tab',
    body: 'AI-generated improvement plan, sequenced into 90-day phases, tailored to your weakest pillars. Each phase has a focus, expected outcome, and ROI uplift. What to do: stop trying to fix everything at once — pick the first phase, run it for the quarter, then reassess.',
  },
  benchmark: {
    title: 'Benchmark tab',
    body: "How your maturity compares to peers in your industry and overall. Shows pillar-by-pillar gaps so you can see where you're ahead and where you're trailing. What to do: ignore pillars where you already lead — focus board conversations on the 1-2 pillars where the peer gap is largest.",
  },
  analysis: {
    title: 'Analysis tab',
    body: 'Visual decomposition of your maturity: radar chart for shape, pillar bars for absolute scores, heatmap for dimension-level detail. What to do: use this when you need to pinpoint *which dimension inside a pillar* is dragging the score — a 50 in Strategic & Governance often means one weak dimension, not five mediocre ones.',
  },
  financial: {
    title: 'Financial tab',
    body: 'Translates maturity into dollars: ROI leakage today, the ROI uplift of advancing one band, and the maturity factor applied to your value-chain calculations. What to do: use the leakage number with finance leaders to justify the investment case for governance + transfer-validation work.',
  },
  history: {
    title: 'History tab',
    body: 'Timeline of every assessment ever submitted for this org — who took it, when, and the resulting score. Click any row for the full snapshot (answers, pillar breakdown, question text). What to do: compare the current snapshot against the last assessment to see exactly which dimensions moved.',
  },
  report: {
    title: 'Report Card tab',
    body: 'Board-ready, one-page summary of maturity, gaps, financial impact, and recommendations. Exportable as PDF / DOCX / PPTX. What to do: generate before quarterly business reviews — the page is designed to be dropped into a board deck without further editing.',
  },
};

function getBandColor(band) {
  if (band === 'Optimized') return 'text-cyan-600';
  if (band === 'Managed') return 'text-teal-600';
  if (band === 'Developing') return 'text-amber-600';
  return 'text-slate-600';
}

function getBandBg(band) {
  if (band === 'Optimized') return 'bg-cyan-50 border-cyan-200';
  if (band === 'Managed') return 'bg-teal-50 border-teal-200';
  if (band === 'Developing') return 'bg-amber-50 border-amber-200';
  return 'bg-slate-50 border-slate-200';
}

function getBand(score) {
  if (score >= 75) return 'Optimized';
  if (score >= 50) return 'Managed';
  if (score >= 25) return 'Developing';
  return 'Emerging';
}

function getBarColor(score) {
  if (score >= 75) return '#06b6d4';
  if (score >= 50) return '#14b8a6';
  if (score >= 25) return '#f59e0b';
  return '#94a3b8';
}

// AA-legible variants of getBarColor for LIGHT mode (small text on white).
// Surfaced as a --bl CSS var on `.mc-band` elements so light mode can swap
// to these while dark mode keeps the brighter getBarColor inline value.
function getBarColorLight(score) {
  if (score >= 75) return '#0e7490';
  if (score >= 50) return '#0f766e';
  if (score >= 25) return '#b45309';
  return '#475569';
}

const SHORT_NAMES = {
  'Learning Demand & Productization': 'Demand',
  'Skills & Capability Alignment': 'Skills',
  'Governance & Operational Efficiency': 'Governance',
  'Insight & Value Demonstration': 'Insight',
  'Adoption & Behavioral Impact': 'Adoption',
};

// ────── Insights Tab ──────
function InsightsTab({ data }) {
  const navigate = useNavigate();
  const { band_insights, roi_leakage_range, indicators, primary_constraint, maturity_band, overall_score, uplift } = data;
  const [showConstraintInfo, setShowConstraintInfo] = useState(false);
  const [expandedSymptom, setExpandedSymptom] = useState(null);

  const SYMPTOM_DETAILS = {
    'Content Portfolio Rationalization': { driver: 'Learning portfolio has grown organically without strategic curation, leading to duplication and outdated content.', impact: 'Learners waste time on irrelevant content, reducing engagement and inflating cost-per-completion without measurable skill gains.' },
    'Learning SKU & Value Proposition Clarity': { driver: 'Learning offerings lack clear product definitions and articulated business value, making it hard to measure or communicate ROI.', impact: 'Stakeholders cannot tie learning to business outcomes, making budget justification difficult and ROI calculations unreliable.' },
    'Metadata, Search & Discoverability': { driver: 'Poor metadata standards and search capabilities mean learners cannot find relevant content efficiently.', impact: 'Low utilization rates despite high content investment. Time-to-competency increases as learners struggle to find what they need.' },
    'Content Lifecycle Governance': { driver: 'No formal review cycles mean outdated or redundant content persists, eroding trust in the learning platform.', impact: 'Learner confidence drops, voluntary adoption declines, and training effectiveness decreases as content quality degrades over time.' },
    'Demand Generation & Promotion': { driver: 'Learning programs are not proactively marketed to the workforce, relying on compliance mandates rather than demand.', impact: 'Voluntary adoption stays low, limiting the reach and business impact of strategic learning investments.' },
    'Program Performance Measurement': { driver: 'Measurement stops at completion rates. No multi-dimensional assessment of learning effectiveness.', impact: 'Cannot identify which programs drive business outcomes, leading to continued investment in ineffective training.' },
    'Skills Framework Definition': { driver: 'No formal skills taxonomy exists, making it impossible to systematically identify and close capability gaps.', impact: 'Learning investment is misaligned with actual skill needs, resulting in wasted budget and persistent readiness gaps.' },
    'Skills Gap Visibility': { driver: 'Gaps are reported anecdotally by managers rather than measured systematically across the organization.', impact: 'Critical skill shortages remain hidden until they cause business failures, making remediation reactive and expensive.' },
    'Learning-to-Skill Mapping': { driver: 'Content is not mapped to specific skills or proficiency levels, so completing training does not reliably close skill gaps.', impact: 'Training completions do not translate to measurable capability improvement, undermining the entire LEI-to-KPI chain.' },
    'Workforce Readiness Measurement': { driver: 'Readiness is proxied by training completion rather than actual capability assessment.', impact: 'False confidence in workforce preparedness. Business initiatives launch with undertrained teams, impacting quality and safety KPIs.' },
    'Strategic Workforce Alignment': { driver: 'Learning planning operates independently from business strategy and workforce planning cycles.', impact: 'Training addresses yesterday\'s needs. Strategic initiatives lack the skilled workforce required for success.' },
    'Skills-Based Reporting to Leadership': { driver: 'Leadership receives no regular visibility into organizational skill health or learning ROI.', impact: 'Learning budget is treated as discretionary cost, vulnerable to cuts. Strategic influence of L&D erodes over time.' },
    'Learning Operations Standardization': { driver: 'Each team runs learning operations independently with no shared standards or processes.', impact: 'Inconsistent learner experience, duplicated effort, and inability to benchmark or optimize at scale.' },
    'Scheduling & Resource Optimization': { driver: 'Manual scheduling leads to frequent conflicts, underutilized instructors, and wasted facility costs.', impact: 'Direct cost waste from idle resources. Learners face scheduling barriers that reduce participation and completion rates.' },
    'Automation of Administrative Tasks': { driver: 'Administrative processes remain manual — registrations, reminders, certifications all require human intervention.', impact: 'Admin overhead consumes L&D budget that could fund strategic initiatives. Response times frustrate learners.' },
    'Scalability of Delivery Model': { driver: 'Delivery depends heavily on instructor availability, limiting reach and creating bottlenecks.', impact: 'Cannot scale training to meet demand spikes. Business units wait for training, delaying strategic initiatives.' },
    'Data Integrity & System Governance': { driver: 'Multiple disconnected systems with no data governance create inconsistent, unreliable learning records.', impact: 'Analytics are untrustworthy. Compliance reporting is error-prone. ROI calculations built on bad data lose credibility.' },
    'Cost Efficiency Management': { driver: 'No visibility into true learning costs per program, per learner, or per outcome.', impact: 'Cannot identify waste or optimize spend. Budget decisions are made without cost-effectiveness data.' },
    'Learning Analytics Maturity': { driver: 'Analytics limited to basic LMS reports — completions, hours, satisfaction. No diagnostic or predictive capability.', impact: 'Cannot explain why programs succeed or fail. No early warning system for training effectiveness issues.' },
    'KPI Linkage': { driver: 'No statistical linkage between learning metrics and business performance indicators.', impact: 'The entire LEI-to-KPI-to-ROI chain is broken. Learning impact remains anecdotal rather than evidence-based.' },
    'Executive Reporting': { driver: 'No regular, strategic reports reach executive leadership. Reports are activity-based rather than outcome-based.', impact: 'Executives see learning as an overhead function. Budget and strategic influence are limited.' },
    'Return on Expectation (ROE) Measurement': { driver: 'Measurement stops at satisfaction (L1). No assessment of behavior change (L3) or business results (L4).', impact: 'Cannot demonstrate that training changes behavior or drives results. Stakeholder confidence in learning erodes.' },
    'ROI Visibility': { driver: 'ROI is not calculated for any programs. Learning investment returns are entirely opaque.', impact: 'Cannot justify budget increases. Cannot identify high-ROI vs low-ROI programs for portfolio optimization.' },
    'Continuous Value Optimization': { driver: 'No systematic process to review and improve program effectiveness based on outcomes data.', impact: 'Programs remain static even as business needs evolve. Value delivery degrades over time without optimization.' },
    'Learner Adoption Rates': { driver: 'Low voluntary adoption indicates learners do not see value in available programs.', impact: 'Learning investment reaches only compliance-mandated audiences. Strategic programs underperform on reach and impact.' },
    'Manager Reinforcement': { driver: 'Managers do not follow up on team learning or reinforce application of new skills on the job.', impact: 'Knowledge acquired in training decays rapidly without reinforcement. Transfer to job performance is minimal.' },
    'Behavior Change Measurement': { driver: 'No measurement of whether training changes on-the-job behavior. Only completion and satisfaction are tracked.', impact: 'The critical link between learning and performance is invisible. ROI claims lack behavioral evidence.' },
    'Time-to-Competency': { driver: 'No tracking of how long it takes new or upskilled employees to reach full productivity.', impact: 'Cannot demonstrate learning\'s impact on ramp time. Optimization opportunities are missed.' },
    'Retention & Capability Sustainability': { driver: 'Skills decay quickly after training with no reinforcement or continuous learning pathways.', impact: 'Repeated training costs for the same skills. Capability gains are temporary rather than sustained.' },
    'Learning Culture Strength': { driver: 'Learning is not valued culturally. It is seen as time away from "real work" rather than a strategic investment.', impact: 'Adoption remains compliance-driven. The organization cannot develop the learning agility needed for competitive advantage.' },
  };

  return (
    <div className="space-y-6" data-testid="insights-tab">
      {/* What maturity means */}
      <Card>
        <CardContent className="pt-5">
          <h3 className="text-base font-semibold text-slate-800 mb-1">
            <span className={cn("inline-block w-2.5 h-2.5 rounded-full mr-2",
              maturity_band === 'Optimized' ? 'bg-cyan-500' : maturity_band === 'Managed' ? 'bg-teal-500' : maturity_band === 'Developing' ? 'bg-amber-500' : 'bg-slate-400'
            )} />
            What "{maturity_band}" Maturity Means
          </h3>
          <p className="text-sm text-slate-500 mb-4">Your maturity score of {overall_score} places you in the {maturity_band} maturity band</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { key: 'structural_capability', label: 'Structural Capability', icon: Settings2 },
              { key: 'financial_realization', label: 'Financial Realization', icon: TrendingUp },
              { key: 'risk_profile', label: 'Risk Profile', icon: AlertTriangle },
              { key: 'strategic_positioning', label: 'Strategic Positioning', icon: Target },
            ].map(({ key, label, icon: Icon }) => (
              <div key={key} className="p-3.5 rounded-lg border border-slate-200 bg-white">
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-800">{label}</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{band_insights[key]}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ROI Leakage */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="roi-leakage-warning">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Estimated ROI Leakage: {roi_leakage_range}</p>
            <p className="text-sm text-amber-700 mt-0.5">At {maturity_band} maturity, approximately {roi_leakage_range} of your learning investment's potential ROI may be unrealized due to structural and operational gaps.</p>
          </div>
        </div>
      </div>

      {/* Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: 'process_maturity', label: 'Process Maturity' },
          { key: 'roi_predictability', label: 'ROI Predictability' },
          { key: 'executive_visibility', label: 'Executive Visibility' },
          { key: 'strategic_integration', label: 'Strategic Integration' },
        ].map(({ key, label }) => (
          <div key={key} className={cn(
            "p-4 rounded-lg border-2 text-center transition-all shadow-sm",
            indicators[key]
              ? 'border-emerald-300 bg-emerald-50'
              : 'border-rose-200 bg-rose-50'
          )} data-testid={`indicator-${key}`}>
            {indicators[key]
              ? <CheckCircle2 className="h-7 w-7 text-emerald-600 mx-auto mb-1.5" />
              : <XCircle className="h-7 w-7 text-rose-400 mx-auto mb-1.5" />}
            <p className={cn("text-xs font-bold uppercase tracking-wider", indicators[key] ? 'text-emerald-700' : 'text-rose-600')}>{label}</p>
            <p className={cn("text-[10px] mt-0.5 font-medium", indicators[key] ? 'text-emerald-500' : 'text-rose-400')}>
              {indicators[key] ? 'Achieved' : 'Not Yet'}
            </p>
          </div>
        ))}
      </div>

      {/* Primary Constraint */}
      {primary_constraint && (
        <Card className="border-2 border-rose-300 shadow-sm" data-testid="primary-constraint">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold text-rose-800 flex items-center gap-2">
                  <AlertTriangle className="h-4.5 w-4.5" /> Primary Constraint Identified
                </h3>
                <p className="text-sm text-rose-600 mt-0.5">Your lowest-scoring pillar is limiting overall ROI realization</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-rose-600">{Math.round(100 - primary_constraint.score * 1.2)}%</span>
                <p className="text-xs text-rose-500">ROI at Risk</p>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 mb-3">
              <div className="flex items-center gap-3">
                <span className="text-xl font-extrabold text-rose-500">{Math.round(primary_constraint.score)}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{primary_constraint.name}</p>
                  <p className="text-xs text-rose-500 font-medium">Constraint Pillar</p>
                </div>
              </div>
            </div>

            <button onClick={() => navigate('/maturity-health/roadmap')} data-testid="primary-constraint-action"
              className="w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors mb-3">
              <Zap className="h-4 w-4" /> Take Action <ArrowRight className="h-4 w-4" />
            </button>

            {/* More Information expandable */}
            <button
              onClick={() => setShowConstraintInfo(!showConstraintInfo)}
              className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium mb-3 transition-colors"
              data-testid="constraint-more-info-btn"
            >
              <Lightbulb className="h-3.5 w-3.5" />
              {showConstraintInfo ? 'Hide details' : 'Why is this a constraint? What does it mean?'}
              {showConstraintInfo ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {showConstraintInfo && (
              <div className="p-3.5 bg-indigo-50 border border-indigo-200 rounded-lg mb-3 text-sm text-indigo-900 leading-relaxed space-y-2" data-testid="constraint-info-panel">
                <p><strong>Why this is your primary constraint:</strong> {primary_constraint.name} scored {Math.round(primary_constraint.score)}/100 — the lowest of your 5 pillars. In the LEI framework, each pillar contributes to overall learning effectiveness. A weak pillar creates a bottleneck that limits how effectively learning investments translate into business outcomes, regardless of how strong other pillars are.</p>
                <p><strong>What this means for you:</strong> Even if you invest heavily in other areas, the returns will be capped by this constraint. Think of it like a chain — the weakest link determines the overall strength. Improving this pillar will have the highest marginal impact on your overall maturity score and ROI realization.</p>
                <p><strong>Financial impact:</strong> At your current score, approximately {Math.round(100 - primary_constraint.score * 1.2)}% of the potential ROI from learning investments related to this pillar is at risk of being unrealized.</p>
              </div>
            )}

            {primary_constraint.symptoms.length > 0 && (
              <>
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Common Symptoms</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {primary_constraint.symptoms.map((s) => {
                    const details = SYMPTOM_DETAILS[s];
                    const isExpanded = expandedSymptom === s;
                    return (
                      <div key={s} className="rounded-lg border border-slate-200 overflow-hidden transition-all" data-testid={`symptom-${s}`}>
                        <button
                          onClick={() => setExpandedSymptom(isExpanded ? null : s)}
                          className="w-full flex items-center justify-between p-2.5 text-left hover:bg-slate-50 transition-colors"
                        >
                          <span className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                            <ChevronDown className={cn("h-3.5 w-3.5 text-slate-400 transition-transform", isExpanded && "rotate-180")} /> {s}
                          </span>
                        </button>
                        {isExpanded && details && (
                          <div className="px-3 pb-3 space-y-2 border-t border-slate-100 bg-slate-50/50">
                            <div className="pt-2">
                              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5">What drives this</p>
                              <p className="text-sm text-slate-600 leading-relaxed">{details.driver}</p>
                            </div>
                            <div>
                              <p className="text-xs font-bold text-rose-500 uppercase tracking-wider mb-0.5">Impact on ROI</p>
                              <p className="text-sm text-slate-600 leading-relaxed">{details.impact}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ────── Roadmap Tab ──────
const fmtGenerated = (iso) => (iso ? new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function RoadmapTab({ data, onGenerate, loading, onToggleAction, onAssign, owners = [], canAssign = false, canTick = true }) {
  const [expandedPhase, setExpandedPhase] = useState(0);
  if (data === undefined) return <div className="flex justify-center py-12" data-testid="roadmap-tab"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (!data?.roadmap) {
    return (
      <div className="text-center py-12" data-testid="roadmap-tab">
        <Map className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500 mb-3">Generate an AI-powered improvement roadmap tailored to your assessment results. It is kept until you ask for a refresh.</p>
        <Button onClick={() => onGenerate(false)} disabled={loading} className="bg-teal-600 hover:bg-teal-700" data-testid="generate-roadmap-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Brain className="h-4 w-4 mr-1" />}
          Generate Roadmap
        </Button>
      </div>
    );
  }
  const { roadmap, constraint } = data;
  const needsRefresh = data.stale || data.assessment_changed;
  const staleText = data.assessment_changed
    ? `A newer maturity assessment has been recorded since this roadmap was generated${data.assessment_label ? ` (built from “${data.assessment_label}”)` : ''}.`
    : `This roadmap is ${data.age_days} days old — older than ${data.stale_days} days.`;
  const freshness = (
    <div className={cn('flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs', needsRefresh ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-600')} data-testid="roadmap-freshness" data-stale={needsRefresh ? 'true' : 'false'}>
      <span className="inline-flex items-center gap-1.5">{needsRefresh ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> : <Clock className="h-3.5 w-3.5" />}<span data-testid="roadmap-generated-at">Generated {fmtGenerated(data.generated_at)}{data.generated_by ? ` by ${data.generated_by}` : ''}{data.age_days != null ? ` · ${data.age_days === 0 ? 'today' : `${data.age_days}d ago`}` : ''}</span>{needsRefresh && <span className="font-semibold" data-testid="roadmap-stale-text">— {staleText} Refresh to regenerate.</span>}</span>
      <span className="inline-flex items-center gap-2"><span className="inline-flex items-center gap-1.5 font-semibold text-slate-700" data-testid="roadmap-progress-summary"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {data.done_count || 0} of {data.total_actions || 0} actions done</span><span className="h-1.5 w-24 rounded-full bg-slate-200 overflow-hidden"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${data.total_actions ? Math.round(100 * (data.done_count || 0) / data.total_actions) : 0}%` }} /></span></span>
      <Button size="sm" variant={needsRefresh ? 'default' : 'outline'} onClick={() => onGenerate(true)} disabled={loading} className={cn('h-7 text-xs', needsRefresh && 'bg-amber-600 hover:bg-amber-700')} data-testid="refresh-roadmap-btn">
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} {loading ? 'Regenerating…' : 'Refresh roadmap'}
      </Button>
    </div>
  );
  const PHASE_ICONS = [Shield, Zap, TrendingUp];
  const PHASE_COLORS = ['teal', 'amber', 'indigo'];
  return (
    <div className="space-y-4" data-testid="roadmap-tab">
      {freshness}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2"><Map className="h-4.5 w-4.5 text-teal-600" /> Your Improvement Roadmap</h3>
              <p className="text-sm text-slate-500">Prioritized actions to address {constraint?.name || 'constraints'}</p>
            </div>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-700 mb-4">
            <strong>Strategic Priority:</strong> {roadmap.strategic_priority}
          </div>
          <div className="space-y-3">
            {roadmap.phases.map((phase, pi) => {
              const Icon = PHASE_ICONS[pi] || Shield;
              const isExpanded = expandedPhase === pi;
              return (
                <div key={phase.name} className={cn("rounded-lg border transition-all", isExpanded ? 'border-teal-300' : 'border-slate-200')} data-testid={`phase-${pi}`}>
                  <button onClick={() => setExpandedPhase(isExpanded ? -1 : pi)} className="w-full flex items-center justify-between p-3.5 text-left">
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", `bg-${PHASE_COLORS[pi]}-100`)}><Icon className={cn("h-4 w-4", `text-${PHASE_COLORS[pi]}-600`)} /></div>
                      <div>
                        <span className="text-sm font-semibold text-slate-800">Phase {pi + 1}: {phase.name}</span>
                        {pi === 0 && <Badge className="ml-2 text-xs bg-amber-100 text-amber-700 border-amber-200">Quick Wins</Badge>}
                        <p className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" /> {phase.timeframe}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400" data-testid={`roadmap-phase-${pi}-count`}>{phase.actions.filter((_, ai) => data.progress?.[`p${pi}a${ai}`]?.done).length}/{phase.actions.length} done</span>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-2">
                      {phase.quick_win && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">Quick Win</p>
                          <p className="text-sm text-amber-800 font-medium">{phase.quick_win}</p>
                        </div>
                      )}
                      {phase.actions.map((action, ai) => {
                        const pr = data.progress?.[`p${pi}a${ai}`];
                        const done = !!pr?.done;
                        return (
                          <label key={`${phase.name}-action-${ai}`} className={cn('flex items-center gap-3 p-2.5 rounded border transition-colors', done ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-100', canTick && 'cursor-pointer hover:border-teal-200')} data-testid={`roadmap-action-${pi}-${ai}`} data-done={done ? 'true' : 'false'}>
                            <input type="checkbox" checked={done} disabled={!canTick} onChange={(e) => onToggleAction?.(pi, ai, e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0" data-testid={`roadmap-action-${pi}-${ai}-checkbox`} />
                            <span className={cn('w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0', done ? 'bg-emerald-500 text-white' : 'bg-teal-100 text-teal-700')}>{done ? '✓' : ai + 1}</span>
                            <span className={cn('text-sm flex-1 min-w-0', done ? 'text-slate-500 line-through' : 'text-slate-700')}>{action}</span>
                            <span className="flex flex-wrap items-center justify-end gap-1.5 shrink-0" onClick={(e) => e.preventDefault()}>
                              {done && <ActionImpact pr={pr} testId={`roadmap-action-${pi}-${ai}-impact`} />}
                              <ActionOwner pr={pr} owners={owners} canAssign={canAssign} onAssign={(v) => onAssign?.(pi, ai, v)} testId={`roadmap-action-${pi}-${ai}-owner`} />
                              {done && pr?.by && <span className="text-[10px] text-emerald-700 whitespace-nowrap" data-testid={`roadmap-action-${pi}-${ai}-by`}>{pr.by} · {new Date(pr.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>}
                            </span>
                          </label>
                        );
                      })}
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Expected Outcome</p>
                          <p className="text-sm text-slate-700">{phase.expected_outcome}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-1">ROI Impact</p>
                          <p className="text-sm text-slate-700">{phase.roi_impact}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {roadmap.overall_outcome && (
            <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
              <strong>Expected Outcome:</strong> {roadmap.overall_outcome}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ────── Benchmark Tab ──────
const INDUSTRY_LABELS = {
  healthcare: 'Healthcare',
  technology: 'Technology',
  financial_services: 'Financial Services',
  manufacturing: 'Manufacturing',
  retail: 'Retail',
  education: 'Education',
  government: 'Government',
  energy: 'Energy & Utilities',
  telecommunications: 'Telecommunications',
  professional_services: 'Professional Services',
  all: 'All Industries',
};

function BenchmarkTab({ initialData }) {
  const [data, setData] = useState(initialData);
  const [selectedIndustry, setSelectedIndustry] = useState(initialData?.selected_industry || 'all');
  const [loadingBench, setLoadingBench] = useState(false);

  const handleIndustryChange = (industry) => {
    setSelectedIndustry(industry);
    setLoadingBench(true);
    api.get(`/maturity-assessment/benchmark?industry=${industry}`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load benchmark'))
      .finally(() => setLoadingBench(false));
  };

  if (!data?.has_assessment) return <div className="text-center py-12 text-slate-500">No assessment data available.</div>;

  const chartData = data.pillar_comparison.map(p => ({
    name: SHORT_NAMES[p.name] || p.name,
    'Your Score': Math.round(p.your_score),
    'Benchmark Avg': Math.round(p.avg_score),
  }));

  const laggingPillars = data.pillar_comparison.filter(p => p.delta < -5).map(p => SHORT_NAMES[p.name] || p.name);
  const leadingPillars = data.pillar_comparison.filter(p => p.delta > 5).map(p => SHORT_NAMES[p.name] || p.name);
  const weakestPillar = data.pillar_comparison.reduce((a, b) => a.delta < b.delta ? a : b);
  const industryLabel = INDUSTRY_LABELS[selectedIndustry] || selectedIndustry?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'All Industries';

  return (
    <div className="space-y-6" data-testid="benchmark-tab">
      {/* Framework description */}
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <p className="text-sm text-slate-600 leading-relaxed">
          <strong>Benchmark methodology:</strong> Your scores are compared against the <strong>Seertech LEI Maturity Framework</strong>, a structured diagnostic informed by established L&D maturity research including Bersin's Learning Maturity Model, ATD's Capability Model, and Towards Maturity benchmarking methodology. Benchmark averages are calculated from {data.using_fallback ? 'industry-typical reference data' : `${data.sample_size} completed assessments`}{selectedIndustry && selectedIndustry !== 'all' ? ` within the ${industryLabel} sector` : ' across all industries'}. Percentile rankings indicate where your organization sits relative to this peer group.
        </p>
      </div>

      {/* Header card */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <BarChart3 className="h-4.5 w-4.5 text-teal-600" /> Industry Benchmarking
              </h3>
              <p className="text-sm text-slate-500">Compare your LEI against {industryLabel} peers</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedIndustry} onValueChange={handleIndustryChange}>
                <SelectTrigger className="w-48" data-testid="industry-selector">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  {(data.available_industries || []).map(ind => (
                    <SelectItem key={ind} value={ind}>{INDUSTRY_LABELS[ind] || ind.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loadingBench ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="text-center p-3 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Your Score</p>
                  <p className="text-2xl font-bold text-teal-600">{data.your_score}</p>
                </div>
                <div className="text-center p-3 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">{industryLabel} Average</p>
                  <p className="text-2xl font-bold text-slate-600">{data.avg_score}</p>
                </div>
                <div className="text-center p-3 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Delta</p>
                  <p className={cn("text-2xl font-bold", data.delta >= 0 ? 'text-teal-600' : 'text-rose-500')}>{data.delta > 0 ? '+' : ''}{data.delta}</p>
                </div>
                <div className="text-center p-3 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Percentile</p>
                  <p className="text-2xl font-bold text-indigo-600">{data.percentile}th</p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3">
                <Badge className={cn("text-sm px-4 py-1",
                  data.percentile >= 75 ? 'bg-teal-100 text-teal-800 border-teal-300' :
                  data.percentile >= 50 ? 'bg-blue-100 text-blue-800 border-blue-300' :
                  data.percentile >= 25 ? 'bg-amber-100 text-amber-800 border-amber-300' :
                  'bg-rose-100 text-rose-800 border-rose-300'
                )} data-testid="quartile-badge">
                  {data.quartile}{selectedIndustry && selectedIndustry !== 'all' ? ` in ${industryLabel}` : ''}
                </Badge>
                <span className="text-xs text-slate-400">Based on {data.sample_size} assessment{data.sample_size !== 1 ? 's' : ''}{data.using_fallback ? ' (reference data)' : ''}</span>
              </div>

              <div className="max-w-md mx-auto mt-2">
                <PeerBenchmarkTrend stage="maturity" light testId="maturity-benchmark-trend" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pillar comparison chart */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Pillar Comparison vs {industryLabel}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Benchmark Avg" fill="#cbd5e1" radius={[0, 4, 4, 0]} />
              <Bar dataKey="Your Score" fill="#14b8a6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Detailed comparison */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Detailed Comparison</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {data.pillar_comparison.map(p => {
              const isBelow = p.delta < 0;
              return (
                <div key={p.pillar_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100" data-testid={`benchmark-pillar-${p.pillar_id}`}>
                  <div className="flex items-center gap-3">
                    <div className={cn("w-1 h-8 rounded-full", isBelow ? 'bg-rose-400' : p.delta > 0 ? 'bg-teal-400' : 'bg-slate-300')} />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{SHORT_NAMES[p.name] || p.name}</p>
                      <p className="text-xs text-slate-500">{Math.round(p.your_score)} vs {Math.round(p.avg_score)} avg</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={cn("text-sm font-bold", isBelow ? 'text-rose-500' : p.delta > 0 ? 'text-teal-600' : 'text-slate-500')}>
                      {p.delta > 0 ? '+' : ''}{p.delta}
                    </span>
                    <span className="text-xs text-indigo-500 font-medium w-16 text-right">{p.percentile}th %ile</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card data-testid="benchmark-insights">
        <CardHeader className="pb-2"><CardTitle className="text-base">Key Insights</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {data.delta < -10 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-semibold text-amber-800">Significant Growth Opportunity</p>
              <p className="text-sm text-amber-700 mt-0.5">Your maturity score is {Math.abs(data.delta)} points below the {industryLabel} average. Focused improvement could yield substantial competitive advantage in learning effectiveness.</p>
            </div>
          )}
          {data.delta >= -10 && data.delta < 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-800">Near {industryLabel} Benchmark</p>
              <p className="text-sm text-blue-700 mt-0.5">Your maturity score is within {Math.abs(data.delta)} points of the {industryLabel} average. Targeted pillar improvements can move you above average.</p>
            </div>
          )}
          {data.delta >= 0 && (
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="text-sm font-semibold text-teal-800">Above {industryLabel} Benchmark</p>
              <p className="text-sm text-teal-700 mt-0.5">Your maturity score exceeds the {industryLabel} average by {data.delta} points. Continue optimizing to maintain your competitive position.</p>
            </div>
          )}
          {laggingPillars.length > 0 && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
              <p className="text-sm font-semibold text-rose-800">Priority Improvement Areas</p>
              <p className="text-sm text-rose-700 mt-0.5">Focus areas where you lag {industryLabel} peers: <strong>{laggingPillars.join(', ')}</strong>. Closing these gaps will have the highest impact on your overall benchmark position.</p>
            </div>
          )}
          {leadingPillars.length > 0 && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-semibold text-emerald-800">Competitive Strengths</p>
              <p className="text-sm text-emerald-700 mt-0.5">You outperform {industryLabel} peers in: <strong>{leadingPillars.join(', ')}</strong>. These are areas of demonstrated capability that can be leveraged as organizational assets.</p>
            </div>
          )}
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-sm font-semibold text-slate-700">Biggest Gap: {SHORT_NAMES[weakestPillar.name] || weakestPillar.name}</p>
            <p className="text-sm text-slate-600 mt-0.5">Your largest gap to the {industryLabel} benchmark is in <strong>{SHORT_NAMES[weakestPillar.name] || weakestPillar.name}</strong> ({weakestPillar.delta > 0 ? '+' : ''}{weakestPillar.delta} pts). Prioritizing this pillar in your roadmap will yield the fastest benchmark improvement.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ────── Analysis Tab ──────
const PILLAR_NARRATIVES = {
  pillar_1: {
    high: 'Strong content portfolio management means your learning offerings are strategically curated, discoverable, and performance-measured. This directly strengthens LEI scores by ensuring the right content reaches the right people at the right time, maximizing the conversion of learning investment into measurable KPI improvements.',
    mid: 'Your content portfolio has structure but gaps in lifecycle governance and discoverability reduce learning utilization. This dampens LEI effectiveness — learners waste time finding content, and outdated material dilutes training quality, weakening the link between learning investment and KPI movement.',
    low: 'Without portfolio rationalization or demand generation, learning content accumulates reactively with no strategic alignment. This creates significant ROI leakage — investment goes into content that is underutilized, undiscoverable, or misaligned with business needs, breaking the first link in the LEI → KPI chain.',
  },
  pillar_2: {
    high: 'Skills frameworks are dynamic and aligned to business strategy, enabling predictive workforce planning. This amplifies the LEI-to-KPI connection by ensuring learning directly closes capability gaps that drive business outcomes, making ROI attribution clear and defensible.',
    mid: 'Skills gap visibility exists but is inconsistent. Learning-to-skill mapping is partial, meaning some training closes real gaps while other investment misses the mark. This creates uneven KPI impact — some programs drive results while others show no measurable business effect.',
    low: 'Without a skills framework, learning investment cannot be aligned to actual capability needs. Training completions don\'t correlate to skill acquisition, making it impossible to demonstrate that learning drives KPI improvement. ROI claims remain anecdotal rather than evidence-based.',
  },
  pillar_3: {
    high: 'Optimized operations with automation and scalable delivery mean learning reaches the workforce efficiently. This maximizes the throughput of the LEI engine — more effective learning delivered at lower cost per outcome, directly improving ROI ratios.',
    mid: 'Standardized processes exist but scaling and automation gaps create operational drag. Some learning spend goes to administrative overhead rather than capability building, reducing the net ROI of each learning dollar invested.',
    low: 'Fragmented operations with manual processes consume budget that should fund learning itself. High operational costs inflate the investment side of the ROI equation without improving outcomes, making it difficult to demonstrate positive returns on learning spend.',
  },
  pillar_4: {
    high: 'Mature analytics with KPI linkage and executive reporting make the LEI → KPI → ROI chain visible and measurable. Stakeholders can see exactly how learning investment translates to business outcomes, securing ongoing investment and strategic influence.',
    mid: 'Some analytics capability exists but the full chain from learning to business impact is not consistently measured. ROI is calculated for select programs but not portfolio-wide, limiting your ability to optimize investment allocation.',
    low: 'Without analytics beyond completion rates, the connection between learning and business outcomes is invisible. ROI cannot be demonstrated, making learning vulnerable to budget cuts and unable to justify the investment required for maturity improvement.',
  },
  pillar_5: {
    high: 'High adoption with manager reinforcement and behavior change measurement means learning actually transfers to job performance. This is the critical last mile — where LEI improvements convert to actual KPI movement and measurable business ROI.',
    mid: 'Moderate adoption means learning reaches some of the workforce, but without consistent manager reinforcement, knowledge decays before it changes behavior. This creates a transfer gap — LEI scores may improve but KPI impact is diluted.',
    low: 'Low adoption and no behavior change measurement mean learning is not translating to performance. This is the most critical gap in the ROI chain — even excellent content and skills alignment produce no business value if learning doesn\'t change what people do on the job.',
  },
};

function AnalysisTab({ pillarScores }) {
  const [expandedPillar, setExpandedPillar] = useState(null);

  if (!pillarScores?.length) return <div className="text-center py-12 text-slate-500">No assessment data.</div>;
  const radarData = pillarScores.map(ps => ({ subject: SHORT_NAMES[ps.name] || ps.name, score: ps.score, fullMark: 100 }));
  const lowest = pillarScores.reduce((a, b) => a.score < b.score ? a : b);

  const getNarrative = (pillarId, score) => {
    const n = PILLAR_NARRATIVES[pillarId];
    if (!n) return '';
    if (score >= 65) return n.high;
    if (score >= 35) return n.mid;
    return n.low;
  };

  return (
    <div className="space-y-6" data-testid="analysis-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Pillar Performance Radar</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} />
                <Radar dataKey="score" stroke="#0d9488" fill="#0d9488" fillOpacity={0.25} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Pillar Breakdown</CardTitle></CardHeader>
          <CardContent>
            <p className="text-xs text-slate-400 mb-3">Click a pillar to see its impact on your LEI → KPI → ROI chain</p>
            <div className="space-y-2">
              {pillarScores.map(ps => {
                const isConstraint = ps.pillar_id === lowest.pillar_id;
                const isExpanded = expandedPillar === ps.pillar_id;
                return (
                  <div key={ps.pillar_id} data-testid={`analysis-pillar-${ps.pillar_id}`}>
                    <button
                      onClick={() => setExpandedPillar(isExpanded ? null : ps.pillar_id)}
                      className={cn(
                        "w-full text-left p-2.5 rounded-lg border transition-all",
                        isExpanded ? 'border-teal-300 bg-teal-50/30' : isConstraint ? 'border-rose-200 bg-rose-50/30 hover:border-rose-300' : 'border-slate-100 hover:border-slate-300'
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                          <ChevronDown className={cn("h-3.5 w-3.5 text-slate-400 transition-transform", isExpanded && "rotate-180")} />
                          {ps.name}
                        </span>
                        <div className="flex items-center gap-2">
                          {isConstraint && <Badge variant="destructive" className="text-xs">Constraint</Badge>}
                          <span className="text-sm font-bold" style={{ color: getBarColor(ps.score) }}>{Math.round(ps.score)}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden ml-5">
                        <div className="h-full rounded-full transition-all" style={{ width: `${ps.score}%`, backgroundColor: getBarColor(ps.score) }} />
                      </div>
                      <p className={cn("text-xs mt-0.5 ml-5", getBandColor(getBand(ps.score)))}>{getBand(ps.score)}</p>
                    </button>
                    {isExpanded && (
                      <div className="mt-1 ml-5 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 leading-relaxed" data-testid={`narrative-${ps.pillar_id}`}>
                        {getNarrative(ps.pillar_id, ps.score)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Maturity Heatmap */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Maturity Heatmap</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="text-xs text-slate-500">
                  <th className="text-left py-2 w-24 font-medium">Pillar</th>
                  <th className="text-center py-2 font-medium">Emerging<br/><span className="text-[9px] text-slate-400 font-normal">0-24</span></th>
                  <th className="text-center py-2 font-medium">Developing<br/><span className="text-[9px] text-slate-400 font-normal">25-49</span></th>
                  <th className="text-center py-2 font-medium">Managed<br/><span className="text-[9px] text-slate-400 font-normal">50-74</span></th>
                  <th className="text-center py-2 font-medium">Optimized<br/><span className="text-[9px] text-slate-400 font-normal">75-100</span></th>
                </tr>
              </thead>
              <tbody>
                {pillarScores.map(ps => {
                  const band = getBand(ps.score);
                  const bandIdx = ['Emerging', 'Developing', 'Managed', 'Optimized'].indexOf(band);
                  // Calculate position within the band column (0-100%)
                  const bandRanges = [[0, 24], [25, 49], [50, 74], [75, 100]];
                  const [lo, hi] = bandRanges[bandIdx];
                  const posInBand = Math.max(0, Math.min(100, ((ps.score - lo) / (hi - lo)) * 100));
                  return (
                    <tr key={ps.pillar_id} className="border-t border-slate-100">
                      <td className="py-2.5 text-sm font-medium text-slate-700">{SHORT_NAMES[ps.name] || ps.name}</td>
                      {['Emerging', 'Developing', 'Managed', 'Optimized'].map((b, bi) => (
                        <td key={b} className="py-2.5 px-1">
                          <div className={cn("relative h-8 rounded", bi === bandIdx ? '' : 'bg-slate-50')}
                            style={bi === bandIdx ? { backgroundColor: `${getBarColor(ps.score)}12` } : undefined}>
                            {bi === bandIdx && (
                              <div
                                className="absolute top-1/2 -translate-y-1/2 px-2.5 py-0.5 rounded-full text-white text-xs font-bold whitespace-nowrap"
                                style={{
                                  backgroundColor: getBarColor(ps.score),
                                  left: `${Math.max(5, Math.min(posInBand, 80))}%`,
                                }}
                              >
                                {Math.round(ps.score)}
                              </div>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-400 inline-block" /> Emerging (0-24)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500 inline-block" /> Developing (25-49)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-teal-500 inline-block" /> Managed (50-74)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-cyan-500 inline-block" /> Optimized (75-100)</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ────── Financial Tab ──────
const PILLAR_FINANCIAL_NARRATIVES = {
  pillar_1: {
    impact: 'Learning Demand & Productization drives the foundation of your learning ROI. When content is not strategically curated, discoverable, or measured, learners receive training that doesn\'t align with business needs — every dollar spent on misaligned content is a dollar wasted.',
    actions: ['Rationalize your content portfolio — retire outdated programs and invest in high-impact, strategically aligned offerings', 'Implement content lifecycle governance with automated sunset dates and usage-based reviews', 'Build demand generation campaigns targeting voluntary adoption of strategic programs'],
  },
  pillar_2: {
    impact: 'Skills & Capability Alignment determines whether learning actually closes the gaps that matter. Without skills-based targeting, training is delivered broadly rather than precisely — reducing the conversion rate from learning investment to measurable capability improvement.',
    actions: ['Deploy a skills taxonomy and map all learning content to specific skills and proficiency levels', 'Implement real-time skills gap dashboards visible to L&D and business leaders', 'Create role-based learning pathways that close identified gaps in priority order'],
  },
  pillar_3: {
    impact: 'Governance & Operational Efficiency represents the cost of running your learning operation. Inefficient operations inflate the denominator of your ROI equation — you spend more to deliver the same outcomes, eroding returns even when learning content is effective.',
    actions: ['Automate administrative tasks (registrations, reminders, certifications) to free budget for strategic programs', 'Consolidate learning systems and establish data governance to improve analytics reliability', 'Implement activity-based costing to identify and eliminate low-value operational spend'],
  },
  pillar_4: {
    impact: 'Insight & Value Demonstration is how you prove the LEI → KPI → ROI chain. Without analytics linking learning to business outcomes, stakeholders cannot see the return, making learning vulnerable to budget cuts and preventing data-driven optimization.',
    actions: ['Establish statistical correlations between learning metrics and business KPIs for your top 5 programs', 'Build executive dashboards showing learning\'s contribution to business outcomes monthly', 'Implement Kirkpatrick L3-L4 measurement for strategic initiatives to demonstrate behavior change and business results'],
  },
  pillar_5: {
    impact: 'Adoption & Behavioral Impact is the critical last mile. Even perfect content with perfect skill alignment produces zero ROI if learners don\'t engage and managers don\'t reinforce. This pillar determines whether learning investment converts to actual performance change.',
    actions: ['Implement manager reinforcement programs — equip managers to coach learning application on the job', 'Measure behavior change (not just completions) using multi-rater assessments and performance data', 'Create spaced reinforcement programs for critical skills to combat knowledge decay'],
  },
};

function FinancialTab({ data }) {
  const [showEfficiencyDetail, setShowEfficiencyDetail] = useState(false);
  const [expandedFinPillar, setExpandedFinPillar] = useState(null);

  if (!data?.has_assessment) return <div className="text-center py-12 text-slate-500">No assessment data.</div>;

  const band = data.maturity_band;
  const wastePct = data.roi_leakage.mid_pct;

  return (
    <div className="space-y-6" data-testid="financial-tab">
      {/* ROI Leakage */}
      <Card className="border-rose-200">
        <CardContent className="pt-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-rose-800 flex items-center gap-2"><AlertTriangle className="h-4.5 w-4.5" /> ROI Leakage</h3>
              <p className="text-sm text-rose-600 mt-0.5">Potential unrealized return on your learning investment</p>
            </div>
            <span className="text-3xl font-bold text-rose-600">{data.roi_leakage.range}</span>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">{data.roi_leakage.description}</p>
          <div className="mt-3 h-3 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-rose-400 to-rose-500" style={{ width: `${wastePct}%` }} />
          </div>
          <div className="flex justify-between text-xs text-slate-400 mt-1">
            <span>0% leakage</span>
            <span>50% leakage</span>
          </div>
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800"><strong>What this means:</strong> For every $100,000 of learning investment, approximately <strong>${Math.round(wastePct * 1000)}</strong> of potential business value is being lost to structural and operational maturity gaps. This isn't wasted spend — it's unrealized potential that improving maturity can unlock.</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Maturity Factor */}
        <Card>
          <CardContent className="pt-5">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><Activity className="h-4 w-4 text-teal-600" /> Maturity Factor in Calculations</h3>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-bold text-slate-900">{data.maturity_factor.current}</span>
              <span className="text-sm text-slate-500">current factor</span>
            </div>
            <p className="text-sm text-slate-600 mb-3">This multiplier is applied to <strong>every ROI projection, scenario, and business case</strong> in the platform. It determines how much of your projected learning impact actually materializes as business value.</p>

            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg mb-3">
              <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-1">Cause & Effect</p>
              <p className="text-sm text-indigo-800">A factor of <strong>{data.maturity_factor.current}</strong> means your scenarios project <strong>{Math.round(data.maturity_factor.current * 100)}%</strong> of theoretical maximum ROI. {data.maturity_factor.current < 0.8 ? 'Increasing maturity is the single highest-leverage action to improve all projected outcomes simultaneously.' : 'Your maturity is translating most of your learning potential into projected returns.'}</p>
            </div>

            {data.maturity_factor.next_band && (
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200 mb-3">
                <p className="text-sm text-teal-800">
                  At <strong>{data.maturity_factor.next_band}</strong> maturity, your factor would be <strong>{data.maturity_factor.at_next_band}</strong>
                  {data.maturity_factor.improvement_pct !== 0 && (
                    <span> — improving all projected ROI by <strong className="text-teal-700">~{Math.abs(data.maturity_factor.improvement_pct)}%</strong></span>
                  )}
                </p>
              </div>
            )}

            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">Priority Action</p>
              <p className="text-sm text-slate-700">{data.maturity_factor.current >= 0.8 ? 'Focus on maintaining your high maturity. Monitor for regression in your constraint pillar and continue optimizing operational efficiency.' : 'Improving your weakest pillar score by 15-20 points could shift your maturity band, increasing this factor across all calculations. Start with the Roadmap tab for a phased plan.'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Investment Efficiency */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><DollarSign className="h-4 w-4 text-teal-600" /> Investment Efficiency</h3>
              <button
                onClick={() => setShowEfficiencyDetail(!showEfficiencyDetail)}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                data-testid="efficiency-detail-btn"
              >
                <Lightbulb className="h-3 w-3" />
                {showEfficiencyDetail ? 'Hide details' : 'What does this mean?'}
              </button>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-bold text-slate-900">{data.investment_efficiency.current_pct}%</span>
              <span className="text-sm text-slate-500">effective utilization</span>
            </div>
            <p className="text-sm text-slate-600 mb-3">Of every dollar spent on learning, <strong>{data.investment_efficiency.current_pct}%</strong> is translating into business outcomes. The remaining <strong>{(100 - data.investment_efficiency.current_pct).toFixed(0)}%</strong> is absorbed by structural and operational gaps.</p>

            {showEfficiencyDetail && (
              <div className="space-y-2 mb-3" data-testid="efficiency-detail-panel">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">Impact on Your Organization</p>
                  <p className="text-sm text-amber-800">At {data.investment_efficiency.current_pct}% efficiency, your learning function is operating below its potential. This means stakeholders are funding programs that could deliver {Math.round((100 / data.investment_efficiency.current_pct - 1) * 100)}% more value without additional spend — if maturity gaps were closed.</p>
                </div>
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-1">Stakeholder Message</p>
                  <p className="text-sm text-indigo-800">Frame it this way: "We don't need more budget — we need to unlock the value we're already investing. By improving operational maturity, we can deliver {Math.round((100 / data.investment_efficiency.current_pct - 1) * 100)}% more business value from the same learning spend."</p>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">What You Can Do</p>
                  <p className="text-sm text-slate-700">1. Use the <strong>Roadmap</strong> tab for a phased improvement plan. 2. Focus on your <strong>constraint pillar</strong> — it's the biggest bottleneck to efficiency. 3. Present the efficiency gap to stakeholders as an investment in capability, not cost.</p>
                </div>
              </div>
            )}

            {data.maturity_factor.next_band && (
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-sm text-teal-800">
                  At <strong>{data.maturity_factor.next_band}</strong> maturity: <strong>{data.investment_efficiency.at_next_band}%</strong> efficiency
                  <span className="text-teal-600 font-medium"> (+{(data.investment_efficiency.at_next_band - data.investment_efficiency.current_pct).toFixed(1)} pts)</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pillar ROI Impact */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4.5 w-4.5 text-teal-600" /> Pillar ROI Impact</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-1">Each pillar contributes differently to overall ROI realization. Click a pillar to see its specific impact on the LEI → KPI → ROI chain and what you can do about it.</p>
          <p className="text-xs text-slate-400 mb-3">Pillars are ordered by leakage — highest leakage = highest priority for financial improvement.</p>
          <div className="space-y-2">
            {[...data.pillar_scores].sort((a, b) => a.score - b.score).map(ps => {
              const leakage = Math.max(5, Math.round(50 - ps.score * 0.5));
              const isExpanded = expandedFinPillar === ps.pillar_id;
              const narrative = PILLAR_FINANCIAL_NARRATIVES[ps.pillar_id];
              return (
                <div key={ps.pillar_id} data-testid={`financial-pillar-${ps.pillar_id}`}>
                  <button
                    onClick={() => setExpandedFinPillar(isExpanded ? null : ps.pillar_id)}
                    className={cn("w-full text-left p-3 rounded-lg border transition-all",
                      isExpanded ? 'border-teal-300 bg-teal-50/20' : 'border-slate-100 hover:border-slate-300'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <ChevronDown className={cn("h-3.5 w-3.5 text-slate-400 transition-transform flex-shrink-0", isExpanded && "rotate-180")} />
                          <p className="text-sm font-medium text-slate-800">{ps.name}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-1 ml-5">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${ps.score}%`, backgroundColor: getBarColor(ps.score) }} />
                          </div>
                          <span className="text-xs text-slate-500 w-8">{Math.round(ps.score)}</span>
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <span className="text-sm font-bold text-rose-500">~{leakage}%</span>
                        <p className="text-xs text-slate-400">leakage</p>
                      </div>
                    </div>
                  </button>
                  {isExpanded && narrative && (
                    <div className="mt-1 ml-5 space-y-2" data-testid={`financial-narrative-${ps.pillar_id}`}>
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">Impact on LEI → KPI → ROI</p>
                        <p className="text-sm text-slate-700 leading-relaxed">{narrative.impact}</p>
                      </div>
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">Financial Impact at Score {Math.round(ps.score)}</p>
                        <p className="text-sm text-amber-800">This pillar is contributing approximately <strong>~{leakage}% ROI leakage</strong>. {leakage > 30 ? 'This is a critical gap — addressing it should be your top financial priority.' : leakage > 15 ? 'This represents a significant improvement opportunity that would materially improve ROI realization.' : 'This is performing reasonably, but further optimization can still unlock additional value.'}</p>
                      </div>
                      <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                        <p className="text-xs font-bold text-teal-700 uppercase tracking-wider mb-1">What Can I Do About It?</p>
                        <ol className="text-sm text-teal-800 space-y-1.5 mt-1">
                          {narrative.actions.map((action, ai) => (
                            <li key={`${ps.pillar_id}-action-${ai}`} className="flex items-start gap-2">
                              <span className="w-5 h-5 rounded-full bg-teal-200 text-teal-800 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{ai + 1}</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ────── Maturity Trend Chart ──────
const PILLAR_LINE_COLORS = {
  pillar_1: '#f59e0b',
  pillar_2: '#3b82f6',
  pillar_3: '#8b5cf6',
  pillar_4: '#06b6d4',
  pillar_5: '#10b981',
};

// Compute per-pillar delta from oldest → latest assessment so we
// can surface "Capability climbed +21 pts" / "Governance slipped 4
// pts" style movement callouts. Empty / single-row history returns
// an empty list so the caller can collapse the section gracefully.
function computePillarMovements(history) {
  if (!Array.isArray(history) || history.length < 2) return [];
  const sorted = [...history].reverse(); // oldest → newest
  const oldest = sorted[0];
  const latest = sorted[sorted.length - 1];
  if (!oldest?.pillar_scores || !latest?.pillar_scores) return [];
  const oldestById = oldest.pillar_scores.reduce((acc, p) => {
    acc[p.pillar_id] = p;
    return acc;
  }, {});
  return latest.pillar_scores
    .map((p) => {
      const prev = oldestById[p.pillar_id];
      if (!prev) return null;
      return {
        pillar_id: p.pillar_id,
        name: SHORT_NAMES[p.name] || p.name,
        latest_score: Math.round(p.score),
        delta: Math.round(p.score - prev.score),
        color: PILLAR_LINE_COLORS[p.pillar_id] || '#94a3b8',
      };
    })
    .filter(Boolean);
}

// Insights strip — sits above the chart and tells the story in
// numbers before the chart visualizes it. Anchored in three
// sensemaking questions: where am I now (vs benchmark), how am I
// trending (overall delta), and which pillars moved most?
function MaturityTrendInsights({ history, benchmark }) {
  const movements = useMemo(() => computePillarMovements(history), [history]);
  if (!history || history.length === 0) return null;
  const sorted = [...history].reverse();
  const first = sorted[0]?.overall_score || 0;
  const last = sorted[sorted.length - 1]?.overall_score || 0;
  const overallDelta = Math.round(last - first);
  // Sort risers desc and sliders asc so we always lead with the
  // biggest mover and the biggest slipper. Cap at 2 each so the
  // strip stays scannable on mobile.
  const risers = [...movements].filter((m) => m.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 2);
  const sliders = [...movements].filter((m) => m.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, 2);
  const benchDelta = typeof benchmark?.delta === 'number' ? benchmark.delta : null;
  const benchAvg = typeof benchmark?.avg_score === 'number' ? benchmark.avg_score : null;

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3"
      data-testid="maturity-trend-insights"
    >
      {/* Quartile / vs benchmark */}
      <div className="p-3 rounded-md border border-slate-200 bg-slate-50">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">vs Benchmark</p>
        {benchAvg != null ? (
          <>
            <p className="text-2xl font-bold tabular-nums mt-1 text-slate-900">
              {benchDelta != null && (benchDelta > 0 ? '+' : '')}{benchDelta != null ? benchDelta.toFixed(1) : '—'}
              <span className="text-xs font-normal ml-1 text-slate-500">pts vs avg</span>
            </p>
            <p className="text-[11px] text-slate-600 mt-0.5">
              You: <strong>{Math.round(last)}</strong> · Industry avg: <strong>{Math.round(benchAvg)}</strong>
              {typeof benchmark?.percentile === 'number' && (
                <> · <span className="text-teal-700 font-semibold">P{Math.round(benchmark.percentile)}</span></>
              )}
            </p>
          </>
        ) : (
          <p className="text-xs text-slate-500 mt-1">Benchmark loading…</p>
        )}
      </div>

      {/* Overall trend */}
      <div className={cn(
        "p-3 rounded-md border",
        overallDelta > 0 ? "border-emerald-200 bg-emerald-50" : overallDelta < 0 ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50",
      )}>
        <p className={cn(
          "text-[10px] uppercase tracking-wider font-bold",
          overallDelta > 0 ? "text-emerald-700" : overallDelta < 0 ? "text-rose-700" : "text-slate-500",
        )}>
          Trend since first assessment
        </p>
        <p className={cn(
          "text-2xl font-bold tabular-nums mt-1",
          overallDelta > 0 ? "text-emerald-800" : overallDelta < 0 ? "text-rose-800" : "text-slate-700",
        )}>
          {overallDelta > 0 ? '+' : ''}{overallDelta}
          <span className="text-xs font-normal ml-1 opacity-75">pts overall</span>
        </p>
        <p className="text-[11px] text-slate-600 mt-0.5">
          Across {history.length} assessment{history.length > 1 ? 's' : ''} · {Math.round(first)} → {Math.round(last)}
        </p>
      </div>

      {/* Biggest movers */}
      <div className="p-3 rounded-md border border-slate-200 bg-white">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Biggest movers</p>
        {risers.length === 0 && sliders.length === 0 ? (
          <p className="text-xs text-slate-500 mt-1">No pillar deltas tracked yet.</p>
        ) : (
          <ul className="mt-1 space-y-1">
            {risers.map((r) => (
              <li
                key={r.pillar_id}
                className="flex items-center justify-between text-[11px]"
                data-testid={`pillar-mover-${r.pillar_id}`}
              >
                <span className="truncate text-slate-700">
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: r.color }} />
                  {r.name}
                </span>
                <span className="font-bold text-emerald-700 tabular-nums shrink-0">
                  +{r.delta} pts
                </span>
              </li>
            ))}
            {sliders.map((s) => (
              <li
                key={s.pillar_id}
                className="flex items-center justify-between text-[11px]"
                data-testid={`pillar-mover-${s.pillar_id}`}
              >
                <span className="truncate text-slate-700">
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ backgroundColor: s.color }} />
                  {s.name}
                </span>
                <span className="font-bold text-rose-700 tabular-nums shrink-0">
                  {s.delta} pts
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Annotation label on the Maturity Trend line — shows the point-to-point
// score delta above each assessment marker so score jumps are legible.
function TrendDeltaLabel({ x, y, index, data }) {
  const d = data?.[index];
  if (!d || d.delta == null || d.delta === 0) return null;
  const up = d.delta > 0;
  return (
    <text
      x={x}
      y={y - 12}
      textAnchor="middle"
      fontSize={10}
      fontWeight={700}
      fill={up ? '#34d399' : '#fb7185'}
      data-testid={`trend-delta-${index}`}
    >
      {up ? '▲' : '▼'}{up ? '+' : ''}{d.delta}
    </text>
  );
}


function MaturityTrendChart({ history, benchmark }) {
  const [showPillars, setShowPillars] = useState(false);

  // Build chart data from history (oldest first). `delta` marks the
  // score change vs the previous assessment so leaders can tie jumps
  // to interventions (rendered as an annotation on each point).
  const sorted = [...history].reverse();
  const chartData = sorted.map((a, i) => {
    const d = new Date(a.created_at);
    const label = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear().toString().slice(2)}`;
    const overall = Math.round(a.overall_score);
    const prev = i > 0 ? Math.round(sorted[i - 1].overall_score) : null;
    const point = { date: label, overall, delta: prev == null ? null : overall - prev };
    if (a.pillar_scores) {
      a.pillar_scores.forEach(ps => {
        point[ps.pillar_id] = Math.round(ps.score);
      });
    }
    return point;
  });

  // Unique pillar info from the most recent assessment
  const pillarInfo = (sorted[sorted.length - 1]?.pillar_scores || []).map(ps => ({
    id: ps.pillar_id,
    name: SHORT_NAMES[ps.name] || ps.name,
    color: PILLAR_LINE_COLORS[ps.pillar_id] || '#94a3b8',
  }));

  // Score change
  const first = sorted[0]?.overall_score || 0;
  const last = sorted[sorted.length - 1]?.overall_score || 0;
  const delta = Math.round(last - first);

  return (
    <div>
      <MaturityTrendInsights history={history} benchmark={benchmark} />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">{history.length} assessment{history.length > 1 ? 's' : ''}</span>
          {delta !== 0 && (
            <Badge variant="outline" className={cn("text-xs", delta > 0 ? 'text-teal-600 border-teal-200' : 'text-rose-500 border-rose-200')}>
              {delta > 0 ? '+' : ''}{delta} pts since first assessment
            </Badge>
          )}
        </div>
        <button
          onClick={() => setShowPillars(!showPillars)}
          className="text-xs text-teal-600 hover:text-teal-800 font-medium transition-colors"
          data-testid="toggle-pillar-lines"
        >
          {showPillars ? 'Hide pillar breakdown' : 'Show pillar breakdown'}
        </button>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
          <ReferenceArea y1={0} y2={25} fill="#f1f5f9" fillOpacity={0.5} />
          <ReferenceArea y1={25} y2={50} fill="#fef3c7" fillOpacity={0.3} />
          <ReferenceArea y1={50} y2={75} fill="#ccfbf1" fillOpacity={0.3} />
          <ReferenceArea y1={75} y2={100} fill="#cffafe" fillOpacity={0.3} />
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} ticks={[0, 25, 50, 75, 100]} />
          <Tooltip
            contentStyle={{ fontSize: 12 }}
            formatter={(value, name) => {
              const pillar = pillarInfo.find(p => p.id === name);
              if (name === 'Industry avg') return [value, 'Industry avg'];
              return [value, pillar ? pillar.name : 'Overall Score'];
            }}
          />
          <ReferenceLine y={25} stroke="#e2e8f0" strokeDasharray="3 3" />
          <ReferenceLine y={50} stroke="#e2e8f0" strokeDasharray="3 3" />
          <ReferenceLine y={75} stroke="#e2e8f0" strokeDasharray="3 3" />
          {/* Industry-average reference line — anchored on the
              same /maturity-assessment/benchmark payload that
              powers the Benchmark tab. Renders only when we have
              a real number, never a 0-default that would mislead. */}
          {typeof benchmark?.avg_score === 'number' && (
            <ReferenceLine
              y={Math.round(benchmark.avg_score)}
              stroke="#7c3aed"
              strokeDasharray="6 3"
              strokeWidth={1.5}
              label={{
                value: `Industry avg ${Math.round(benchmark.avg_score)}`,
                position: 'insideTopRight',
                fill: '#7c3aed',
                fontSize: 10,
                fontWeight: 600,
              }}
            />
          )}
          <Line type="monotone" dataKey="overall" name="Overall Score" stroke="#0f172a" strokeWidth={3} dot={{ r: 4, fill: '#0f172a' }} activeDot={{ r: 6 }} label={<TrendDeltaLabel data={chartData} />} />
          {showPillars && pillarInfo.map(pi => (
            <Line key={pi.id} type="monotone" dataKey={pi.id} name={pi.id} stroke={pi.color} strokeWidth={1.5} strokeDasharray="4 2" dot={{ r: 2.5, fill: pi.color }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="w-6 h-0.5 bg-slate-200 inline-block" /> Emerging</span>
        <span className="flex items-center gap-1"><span className="w-6 h-0.5 bg-amber-200 inline-block" /> Developing</span>
        <span className="flex items-center gap-1"><span className="w-6 h-0.5 bg-teal-200 inline-block" /> Managed</span>
        <span className="flex items-center gap-1"><span className="w-6 h-0.5 bg-cyan-200 inline-block" /> Optimized</span>
      </div>
      {showPillars && (
        <div className="flex flex-wrap items-center justify-center gap-3 mt-2 text-xs">
          <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-slate-900 inline-block" style={{ height: 3 }} /> Overall</span>
          {pillarInfo.map(pi => (
            <span key={pi.id} className="flex items-center gap-1"><span className="w-4 inline-block" style={{ height: 2, backgroundColor: pi.color, borderTop: `1px dashed ${pi.color}` }} /> {pi.name}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ────── Assessment Snapshot ──────
function AssessmentSnapshot({ assessmentId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/maturity-assessment/detail/${assessmentId}`)
      .then(res => setData(res.data))
      .catch(err => { logger.error('Error loading snapshot:', err); toast.error('Failed to load assessment'); })
      .finally(() => setLoading(false));
  }, [assessmentId]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>;
  if (!data) return <div className="text-center py-8 text-slate-500">Assessment not found.</div>;

  return (
    <div className="space-y-5" data-testid="assessment-snapshot">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onClose} className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-800 font-medium mb-1" data-testid="snapshot-back-btn">
            <ChevronUp className="h-3.5 w-3.5 rotate-[-90deg]" /> Back to History
          </button>
          <h3 className="text-base font-semibold text-slate-800">Assessment Snapshot</h3>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
            {data.assessor_name && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {data.assessor_name}</span>}
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(data.created_at).toLocaleString()}</span>
          </div>
        </div>
        <div className="text-right">
          <span className="text-3xl font-bold" style={{ color: getBarColor(data.overall_score) }}>{Math.round(data.overall_score)}</span>
          <p className={cn("text-sm font-semibold", getBandColor(data.maturity_band))}>{data.maturity_band}</p>
        </div>
      </div>

      {/* Pillar summary bars */}
      <div className="grid grid-cols-5 gap-2">
        {(data.pillar_scores || []).map(ps => (
          <div key={ps.pillar_id} className="p-2 rounded-lg border border-slate-100 text-center">
            <span className="text-lg font-bold" style={{ color: getBarColor(ps.score) }}>{Math.round(ps.score)}</span>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
              <div className="h-full rounded-full" style={{ width: `${ps.score}%`, backgroundColor: getBarColor(ps.score) }} />
            </div>
            <p className="text-[10px] text-slate-500 mt-1 truncate">{SHORT_NAMES[ps.name] || ps.name}</p>
          </div>
        ))}
      </div>

      {/* Detailed Q&A by pillar */}
      {(data.enriched_pillars || []).map(pillar => (
        <Card key={pillar.id}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{pillar.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pillar.dimensions.map((dim, di) => (
                <div key={dim.id} className="pb-3 border-b border-slate-100 last:border-0 last:pb-0" data-testid={`snapshot-dim-${dim.id}`}>
                  <p className="text-sm text-slate-700 mb-1">
                    <span className="text-xs text-slate-400 mr-1">{di + 1}.</span>
                    {dim.question}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold flex-shrink-0" style={{
                      backgroundColor: getBarColor(dim.score),
                      color: 'white'
                    }}>
                      {dim.selected_level}
                    </span>
                    <span className="text-sm text-slate-600">{dim.selected_text || 'Not answered'}</span>
                    <span className="ml-auto text-xs font-mono text-slate-400">{Math.round(dim.score)}/100</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ────── History Tab ──────
function HistoryTab({ history }) {
  const [viewingId, setViewingId] = useState(null);

  if (viewingId) {
    return <AssessmentSnapshot assessmentId={viewingId} onClose={() => setViewingId(null)} />;
  }

  if (!history?.length) {
    return <div className="text-center py-8 text-slate-500">No past assessments found.</div>;
  }
  return (
    <div className="space-y-2" data-testid="history-tab">
      {history.map((a, i) => (
        <div
          key={a.id || i}
          className="flex items-center justify-between p-3.5 rounded-lg border border-slate-200 hover:border-teal-300 hover:shadow-sm cursor-pointer transition-all"
          onClick={() => a.id && setViewingId(a.id)}
          data-testid={`history-item-${i}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: `${getBarColor(a.overall_score)}18`, color: getBarColor(a.overall_score) }}>
              {Math.round(a.overall_score)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn("text-xs", getBandColor(getBand(a.overall_score)))}>{getBand(a.overall_score)}</Badge>
                <span className="text-sm font-medium text-slate-800">{Math.round(a.overall_score)}/100</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                {a.assessor_name && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {a.assessor_name}</span>}
                {a.assessor_email && !a.assessor_name && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {a.assessor_email}</span>}
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(a.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-2">
              {(a.pillar_scores || []).map(ps => (
                <div key={ps.pillar_id} className="text-center" title={ps.name}>
                  <div className="w-8 h-1.5 rounded-full" style={{ backgroundColor: getBarColor(ps.score) }} />
                  <span className="text-[9px] text-slate-400">{Math.round(ps.score)}</span>
                </div>
              ))}
            </div>
            <Eye className="h-4 w-4 text-slate-400" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ────── Report Card Tab ──────
function ReportCardTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [stale, setStale] = useState(false);
  const [loadingCached, setLoadingCached] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/maturity-assessment/report/latest')
      .then((r) => { if (!cancelled && r.data?.report) { setReport(r.data.report); setGeneratedAt(r.data.generated_at); setStale(!!r.data.stale); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingCached(false); });
    return () => { cancelled = true; };
  }, []);

  const generateReport = async () => {
    setLoading(true);
    try {
      const res = await api.post('/maturity-assessment/report/generate', {});
      setReport(res.data);
      setGeneratedAt(res.data?.generated_at || new Date().toISOString());
      setStale(false);
      toast.success('Report generated');
    } catch (err) {
      logger.error('Report generation failed:', err);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const fmtGenerated = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
  };

  const downloadReport = async (format) => {
    setDownloading(format);
    try {
      const res = await api.get(`/maturity-assessment/report/download/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format;
      link.setAttribute('download', `Maturity_Report_Card.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      logger.error(`Download ${format} failed:`, err);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  if (!report) {
    if (loadingCached) {
      return (
        <div className="text-center py-12" data-testid="report-card-tab">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading your latest report…</p>
        </div>
      );
    }
    return (
      <div className="text-center py-12" data-testid="report-card-tab">
        <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">Learning Maturity Report Card</h3>
        <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
          Generate a comprehensive report summarizing your maturity assessment, insights, benchmark position, financial impact, and an AI-powered overall narrative with recommendations.
        </p>
        <Button onClick={generateReport} disabled={loading} className="bg-teal-600 hover:bg-teal-700" data-testid="generate-report-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
          Generate Report Card
        </Button>
      </div>
    );
  }

  const rd = report;
  const band = rd.maturity_band;

  return (
    <div className="space-y-6" data-testid="report-card-tab">
      {/* Download bar */}
      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div>
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            Report Card Ready
            {stale && (
              <button onClick={generateReport} disabled={loading} className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[11px] font-semibold text-amber-800 hover:bg-amber-200 transition-colors disabled:opacity-60" data-testid="report-card-stale-badge" title="A newer assessment exists — click to refresh the report">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Stale — refresh with latest
              </button>
            )}
          </p>
          <p className="text-xs text-slate-500">
            {generatedAt ? <>Last generated {fmtGenerated(generatedAt)} · cached{stale ? ' · regenerate to reflect the latest assessment' : ' (no regeneration needed to read)'}</> : 'Download in your preferred format. All exports include org logo, branding, and clean page breaks.'}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" size="sm" onClick={generateReport} disabled={loading} data-testid="regenerate-report-btn" title="Re-run the AI narrative from the latest numbers">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1 text-teal-600" />}
            Regenerate
          </Button>
          {[
            { fmt: 'pdf', icon: FileText, label: 'PDF', color: 'text-rose-600' },
            { fmt: 'docx', icon: FileSpreadsheet, label: 'Word', color: 'text-blue-600' },
            { fmt: 'pptx', icon: Presentation, label: 'PPT', color: 'text-amber-600' },
          ].map(({ fmt, icon: Icon, label, color }) => (
            <Button
              key={fmt}
              variant="outline"
              size="sm"
              onClick={() => downloadReport(fmt)}
              disabled={downloading === fmt}
              data-testid={`download-${fmt}-btn`}
            >
              {downloading === fmt ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Icon className={cn("h-3.5 w-3.5 mr-1", color)} />}
              {label}
            </Button>
          ))}
        </div>
      </div>

      {/* Cover Section */}
      <Card className="border-2 border-slate-200">
        <CardContent className="pt-6 pb-6 text-center">
          <h2 className="text-2xl font-bold text-slate-900">Learning Maturity Report Card</h2>
          <p className="text-lg text-slate-600 mt-1">{rd.org_name}</p>
          {rd.industry && <p className="text-sm text-slate-500">{rd.industry}</p>}
          <p className="text-xs text-slate-400 mt-2">{rd.generated_date} | Prepared by {rd.assessor_name}</p>
        </CardContent>
      </Card>

      {/* Executive Summary */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Executive Summary</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: `rgb(${BAND_COLORS_RGB[band]})` }}>
              {Math.round(rd.overall_score)}
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{band} Maturity</p>
              <p className="text-sm text-slate-500">{rd.overall_score}/100</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">{rd.executive_summary}</p>
        </CardContent>
      </Card>

      {/* Pillar Overview */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Pillar Scores</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(rd.pillar_scores || []).map(ps => (
              <div key={ps.pillar_id} className="flex items-center gap-3 p-2 rounded border border-slate-100">
                <span className="text-sm font-bold w-8" style={{ color: getBarColor(ps.score) }}>{Math.round(ps.score)}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{ps.name}</p>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full rounded-full" style={{ width: `${ps.score}%`, backgroundColor: getBarColor(ps.score) }} />
                  </div>
                </div>
                <span className={cn("text-xs font-medium", getBandColor(getBand(ps.score)))}>{getBand(ps.score)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Trend */}
      {rd.trend_summary && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Maturity Trend</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">{rd.trend_summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Insights — What "{band}" Maturity Means</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: 'structural_capability', label: 'Structural Capability' },
            { key: 'financial_realization', label: 'Financial Realization' },
            { key: 'risk_profile', label: 'Risk Profile' },
            { key: 'strategic_positioning', label: 'Strategic Positioning' },
          ].map(({ key, label }) => (
            <div key={key} className="p-3 rounded-lg border border-slate-100">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
              <p className="text-sm text-slate-600">{rd.band_insights?.[key]}</p>
            </div>
          ))}
          {rd.primary_constraint && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
              <p className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-1">Primary Constraint</p>
              <p className="text-sm text-rose-800"><strong>{rd.primary_constraint.name}</strong> (Score: {Math.round(rd.primary_constraint.score)}) — This pillar is your biggest bottleneck.</p>
            </div>
          )}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">ROI Leakage</p>
            <p className="text-sm text-amber-800">Estimated {rd.roi_leakage_range} of potential ROI is unrealized at {band} maturity.</p>
          </div>
        </CardContent>
      </Card>

      {/* Benchmark */}
      {rd.benchmark && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Benchmark — {rd.benchmark_industry_label || 'All Industries'}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-3 mb-3">
              <div className="text-center p-2 rounded border"><p className="text-xs text-slate-500">Your Score</p><p className="text-lg font-bold text-teal-600">{rd.benchmark.your_score}</p></div>
              <div className="text-center p-2 rounded border"><p className="text-xs text-slate-500">Average</p><p className="text-lg font-bold text-slate-600">{rd.benchmark.avg_score}</p></div>
              <div className="text-center p-2 rounded border"><p className="text-xs text-slate-500">Delta</p><p className={cn("text-lg font-bold", rd.benchmark.delta >= 0 ? 'text-teal-600' : 'text-rose-500')}>{rd.benchmark.delta > 0 ? '+' : ''}{rd.benchmark.delta}</p></div>
              <div className="text-center p-2 rounded border"><p className="text-xs text-slate-500">Percentile</p><p className="text-lg font-bold text-indigo-600">{rd.benchmark.percentile}th</p></div>
            </div>
            {rd.benchmark.insights && <p className="text-sm text-slate-600">{rd.benchmark.insights}</p>}
          </CardContent>
        </Card>
      )}

      {/* Financial */}
      {rd.financial && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Financial Impact</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg border text-center">
                <p className="text-xs text-slate-500 mb-1">ROI Leakage</p>
                <p className="text-lg font-bold text-rose-500">{rd.financial.roi_leakage_range}</p>
              </div>
              <div className="p-3 rounded-lg border text-center">
                <p className="text-xs text-slate-500 mb-1">Maturity Factor</p>
                <p className="text-lg font-bold text-slate-800">{rd.financial.maturity_factor}</p>
              </div>
              <div className="p-3 rounded-lg border text-center">
                <p className="text-xs text-slate-500 mb-1">Efficiency</p>
                <p className="text-lg font-bold text-teal-600">{rd.financial.efficiency_pct}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ROE Validation */}
      {rd.roe?.has_data && (
        <Card className="border-purple-200">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4.5 w-4.5 text-purple-600" /> ROE — Capability Transfer Validation</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-3">How effectively learning translates into observable workplace behavior change.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="text-center p-2.5 rounded-lg border border-blue-200 bg-blue-50/30">
                <p className="text-xs text-slate-500 mb-0.5">Capability (L2)</p>
                <p className="text-lg font-bold text-blue-600">{rd.roe.capability_pct}%</p>
              </div>
              <div className="text-center p-2.5 rounded-lg border border-amber-200 bg-amber-50/30">
                <p className="text-xs text-slate-500 mb-0.5">Motivation</p>
                <p className="text-lg font-bold text-amber-500">{rd.roe.motivation_pct}%</p>
                <p className="text-[10px] text-slate-400">Intr: {rd.roe.intrinsic_avg}% | Ext: {rd.roe.extrinsic_avg}%</p>
              </div>
              <div className="text-center p-2.5 rounded-lg border border-emerald-200 bg-emerald-50/30">
                <p className="text-xs text-slate-500 mb-0.5">L3 Behavior</p>
                <p className="text-lg font-bold text-emerald-600">{rd.roe.l3_pct}%</p>
              </div>
              <div className="text-center p-2.5 rounded-lg border-2 border-purple-300 bg-purple-50">
                <p className="text-xs text-purple-500 mb-0.5">ROE Score</p>
                <p className="text-lg font-bold text-purple-700">{rd.roe.roe_pct}%</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center p-2 rounded border"><p className="text-xs text-slate-500">PE Ideal</p><p className="text-lg font-bold text-purple-500">{rd.roe.pe_ideal_pct}%</p></div>
              <div className="text-center p-2 rounded border-2 border-purple-300"><p className="text-xs text-purple-500">PE Validated</p><p className="text-lg font-bold text-purple-700">{rd.roe.pe_validated_pct}%</p></div>
              <div className="text-center p-2 rounded border border-rose-200 bg-rose-50/30"><p className="text-xs text-rose-500">Transfer Gap</p><p className="text-lg font-bold text-rose-500">{rd.roe.transfer_gap_pct}%</p></div>
            </div>
            {rd.roe.transfer_gap_pct > 5 && (
              <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
                <strong>{Math.round(100 - rd.roe.roe_pct)}%</strong> of workforce capability is not translating into performance change.
                {rd.roe.motivation_pct < rd.roe.capability_pct
                  ? ' Motivation is the weaker factor — learners have skills but aren\'t applying them.'
                  : ' Capability is the weaker factor — learners are willing but lack sufficient skills.'}
              </div>
            )}
            <p className="text-xs text-slate-400 mt-2">Based on {rd.roe.data_counts?.l2 || 0} L2 records, {rd.roe.data_counts?.motivation || 0} motivation surveys, {rd.roe.data_counts?.l3 || 0} L3 observations.</p>
          </CardContent>
        </Card>
      )}

      {/* Roadmap */}
      {rd.roadmap && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Improvement Roadmap</CardTitle></CardHeader>
          <CardContent>
            {rd.roadmap.strategic_priority && (
              <p className="text-sm text-slate-700 mb-3"><strong>Strategic Priority:</strong> {rd.roadmap.strategic_priority}</p>
            )}
            <div className="space-y-3">
              {(rd.roadmap.phases || []).map(phase => (
                <div key={phase.name} className="p-3 rounded-lg border border-slate-200">
                  <p className="text-sm font-bold text-slate-800">{phase.name} <span className="font-normal text-slate-500">({phase.timeframe})</span></p>
                  <ul className="mt-2 space-y-1">
                    {(phase.actions || []).map((action, ai) => (
                      <li key={`${phase.name}-${ai}`} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="w-4 h-4 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{ai + 1}</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                  {phase.expected_outcome && <p className="text-xs text-slate-500 mt-2"><strong>Outcome:</strong> {phase.expected_outcome}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Overall Narrative */}
      {rd.ai_narrative && (
        <Card className="border-2 border-indigo-200">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Brain className="h-4.5 w-4.5 text-indigo-600" /> Overall Assessment & Recommendations</CardTitle></CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed">
              {rd.ai_narrative.split('\n\n').filter(p => p.trim()).map((para, i) => (
                <p key={`narrative-${i}`} className="text-sm mb-3">{para}</p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regenerate */}
      <div className="flex justify-center pt-2">
        <Button variant="outline" size="sm" onClick={generateReport} disabled={loading} data-testid="regenerate-report-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Brain className="h-3.5 w-3.5 mr-1" />}
          Regenerate Report
        </Button>
      </div>
    </div>
  );
}

const BAND_COLORS_RGB = {
  Emerging: '148,163,184',
  Developing: '245,158,11',
  Managed: '20,184,166',
  Optimized: '6,182,212',
};

// ────── Main Page ──────
// ══════════════════════════════════════════════════════════════
// Lean Overview + Per-Pillar Deep Dives (dark, matches the
// Mission Control Home aesthetic). All panels are explicitly dark
// so they read consistently on the #0a0f1c canvas.
// ══════════════════════════════════════════════════════════════

const PILLAR_ICONS = {
  pillar_1: Lightbulb,
  pillar_2: Zap,
  pillar_3: Target,
  pillar_4: BarChart3,
  pillar_5: Shield,
};

const pillarShort = (name) => SHORT_NAMES[name] || name;

// Deterministic "common symptoms" per pillar for the Primary
// Constraint card (title + one-line detail).
const MATURITY_SYMPTOMS = {
  pillar_1: [
    ['Content sprawl', 'Programs accumulate faster than they are retired or measured.'],
    ['Low discoverability', 'Learners cannot find the right content at the right time.'],
    ['Weak demand signal', 'No structured way to surface what the business actually needs.'],
    ['Utilisation gaps', 'High-value content is under-used while stale material lingers.'],
  ],
  pillar_2: [
    ['No skills taxonomy', 'Learning is not mapped to specific skills or proficiency levels.'],
    ['Blunt targeting', 'Training is delivered broadly instead of to the gaps that matter.'],
    ['Invisible gaps', 'Capability shortfalls are not tracked against business roles.'],
    ['Unclear pathways', 'Learners lack role-based routes to close priority gaps.'],
  ],
  pillar_3: [
    ['Manual overhead', 'Admin tasks consume budget that should fund capability building.'],
    ['System sprawl', 'Fragmented tooling weakens data quality and reporting.'],
    ['Unmanaged cost', 'No activity-based view of where learning spend actually goes.'],
    ['Scaling drag', 'Delivery does not scale without proportional cost increases.'],
  ],
  pillar_4: [
    ['Completion-only metrics', 'Reporting stops at activity, never reaching business impact.'],
    ['No KPI linkage', 'Learning is not statistically tied to the outcomes it should move.'],
    ['Thin exec reporting', 'Leaders cannot see learning\'s contribution to results.'],
    ['No optimisation loop', 'Without evidence, investment cannot be reallocated to what works.'],
  ],
  pillar_5: [
    ['Learner adoption rates', 'Programs reach only part of the workforce they target.'],
    ['Manager reinforcement', 'Managers rarely coach the application of new skills on the job.'],
    ['Behavior change measurement', 'Impact is assumed, not measured beyond completions.'],
    ['Time-to-competency', 'Knowledge decays before it changes day-to-day performance.'],
  ],
};

function DarkCard({ className = '', children, testId }) {
  return (
    <div
      className={cn('rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl', className)}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

// Tone helpers shared by the dark tiles / gauges.
const scoreStroke = (s) => (s >= 75 ? '#22d3ee' : s >= 50 ? '#2dd4bf' : s >= 25 ? '#f59e0b' : '#64748b');
const scoreText = (s) => (s >= 75 ? 'text-cyan-300' : s >= 50 ? 'text-teal-300' : s >= 25 ? 'text-amber-300' : 'text-slate-300');
const bandChipClasses = (s) => (
  s >= 75 ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
    : s >= 50 ? 'bg-teal-500/10 border-teal-500/30 text-teal-300'
      : s >= 25 ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
        : 'bg-slate-500/10 border-slate-500/30 text-slate-300'
);

function ScoreGauge({ score, size = 80, stroke = 8 }) {
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="42" fill="none" stroke="#1f2a44" strokeWidth={stroke} />
        <circle
          cx="50" cy="50" r="42" fill="none"
          stroke={scoreStroke(score)} strokeWidth={stroke}
          strokeDasharray={`${Math.max(0, Math.min(100, score)) * 2.64} 264`}
          strokeLinecap="round"
        />
      </svg>
      <div className={cn('absolute inset-0 flex items-center justify-center text-lg font-bold', scoreText(score))}>
        {Math.round(score)}
      </div>
    </div>
  );
}

function MaturityKpiTiles({ insights, financial, leiQuarterDelta, onSelectPillar }) {
  const { overall_score, maturity_band, uplift, primary_constraint } = insights;
  const eff = financial?.investment_efficiency?.current_pct;
  const roiRisk = primary_constraint ? Math.round(100 - primary_constraint.score * 1.2) : null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="maturity-kpi-tiles">
      {/* Maturity Score */}
      <DarkCard className="p-5" testId="hero-score">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
              Maturity Score
              <TileInfoButton testid="hero-score-info" title={TAB_INFO.weighted_lei.title} body={TAB_INFO.weighted_lei.body} />
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className={cn('text-4xl font-heading font-extrabold tabular-nums', scoreText(overall_score))}>{Math.round(overall_score)}</span>
              <span className="text-sm text-slate-500 font-semibold">/100</span>
            </div>
            <span className={cn('mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', bandChipClasses(overall_score))}>
              {maturity_band}
            </span>
            {leiQuarterDelta && (
              <div
                className={cn(
                  'mt-2 flex items-center gap-1 text-[10px] font-bold',
                  leiQuarterDelta.direction === 'up' && 'text-emerald-400',
                  leiQuarterDelta.direction === 'down' && 'text-rose-400',
                  leiQuarterDelta.direction === 'flat' && 'text-slate-500',
                )}
                data-testid="hero-score-quarter-delta"
              >
                {leiQuarterDelta.direction === 'up' ? '▲' : leiQuarterDelta.direction === 'down' ? '▼' : '▬'}
                {' '}{leiQuarterDelta.delta > 0 ? '+' : ''}{leiQuarterDelta.delta} vs last quarter
              </div>
            )}
            <PeerBenchmarkTrend stage="maturity" testId="maturity-overview-benchmark-trend" />
          </div>
          <ScoreGauge score={overall_score} />
        </div>
      </DarkCard>

      {/* Top Priority */}
      {primary_constraint ? (
        <DarkCard className="p-5 relative overflow-hidden h-full flex flex-col ring-1 ring-rose-500/40 mc-priority-glass shadow-lg shadow-rose-900/20" testId="hero-constraint">
          <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full bg-rose-500/10 blur-2xl" />
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-300">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-400" /> Top Priority
          </div>
          <p className="mt-2 text-2xl font-extrabold text-white leading-tight">{primary_constraint.name}</p>
          <p className="mt-1 text-sm text-rose-300 font-semibold tabular-nums">
            Score {Math.round(primary_constraint.score)} <span className="text-slate-400 font-normal">/ 100</span>
          </p>
          <p className="text-xs text-slate-400 mt-0.5 tabular-nums">ROI Risk: {roiRisk}%</p>
          {onSelectPillar && primary_constraint.pillar_id && (
            <div className="mt-auto pt-4">
              <button onClick={() => onSelectPillar(primary_constraint.pillar_id)} data-testid="hero-constraint-action"
                className="w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white mc-priority-cta hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors">
                <Zap className="h-4 w-4" /> Take Action <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </DarkCard>
      ) : (
        <DarkCard className="p-5" testId="hero-constraint">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            <TrendingUp className="h-3.5 w-3.5 text-[#44b6d2]" /> Top Priority
          </div>
          <p className="mt-2 text-lg font-bold text-slate-100 leading-tight">—</p>
        </DarkCard>
      )}

      {/* Uplift Potential */}
      <DarkCard className="p-5" testId="hero-uplift">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
          <TrendingUp className="h-3.5 w-3.5 text-emerald-400" /> Uplift Potential
        </div>
        <p className="mt-2 text-3xl font-heading font-extrabold text-emerald-300 tabular-nums">+{uplift?.roi_improvement_pct ?? 0}% ROI</p>
        {uplift?.next_band && (
          <p className="text-xs text-slate-400 mt-1">When advancing to {uplift.next_band}</p>
        )}
      </DarkCard>

      {/* Investment Efficiency */}
      <DarkCard className="p-5" testId="hero-efficiency">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
          <DollarSign className="h-3.5 w-3.5 text-[#44b6d2]" /> Investment Efficiency
        </div>
        <p className="mt-2 text-3xl font-heading font-extrabold text-cyan-300 tabular-nums">
          {eff != null ? `${Math.round(eff)}%` : '—'}
        </p>
        <p className="text-xs text-slate-400 mt-1">Effective utilization</p>
      </DarkCard>
    </div>
  );
}

function PillarBreakdownCard({ pillarScores, constraintId, onSelect }) {
  const rows = [...pillarScores].sort((a, b) => b.score - a.score);
  return (
    <DarkCard className="p-5" testId="maturity-pillar-breakdown">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
        Pillar Breakdown
        <TileInfoButton testid="pillar-breakdown-info" title={TAB_INFO.analysis.title} body={TAB_INFO.analysis.body} />
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-4">Click a pillar to see its impact on your LEI → KPI → ROI chain</p>
      <div className="space-y-1">
        {rows.map((ps) => {
          const Icon = PILLAR_ICONS[ps.pillar_id] || Activity;
          const band = getBand(ps.score);
          const color = getBarColor(ps.score);
          const isConstraint = ps.pillar_id === constraintId;
          return (
            <button
              key={ps.pillar_id}
              onClick={() => onSelect(ps.pillar_id)}
              className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.04] transition-colors text-left"
              data-testid={`pillar-row-${ps.pillar_id}`}
            >
              <span className="shrink-0 w-8 h-8 rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center">
                <Icon className="h-4 w-4 text-[#44b6d2]" />
              </span>
              <div className="min-w-0 w-36 sm:w-44 shrink-0">
                <p className="text-sm font-semibold text-slate-100 truncate">{ps.name}</p>
                <p className="text-[11px] font-medium mc-band" style={{ color, '--bl': getBarColorLight(ps.score) }}>{band}</p>
              </div>
              <div className="flex-1 h-2 rounded-full bg-[#0b1220] overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${ps.score}%`, backgroundColor: color }} />
              </div>
              {isConstraint && (
                <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">
                  Constraint
                </span>
              )}
              <span className="shrink-0 w-8 text-right text-lg font-bold tabular-nums mc-band" style={{ color, '--bl': getBarColorLight(ps.score) }}>{Math.round(ps.score)}</span>
              <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-slate-300 transition-colors shrink-0" />
            </button>
          );
        })}
      </div>
    </DarkCard>
  );
}

function PrimaryConstraintCard({ insights, onSelectPillar }) {
  const c = insights.primary_constraint;
  const [showWhy, setShowWhy] = useState(false);
  const [openSym, setOpenSym] = useState(null);
  if (!c) return null;
  const roiRisk = Math.round(100 - c.score * 1.2);
  const symptoms = MATURITY_SYMPTOMS[c.pillar_id] || [];
  return (
    <DarkCard className="p-5 !ring-rose-500/25" testId="maturity-primary-constraint">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-400" />
            <p className="text-sm font-bold text-slate-100">Primary Constraint Identified</p>
          </div>
          <p className="text-xs text-rose-300/90 mt-1">Your lowest-scoring pillar is limiting overall ROI realization</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-3xl font-extrabold tabular-nums text-rose-300 leading-none">{roiRisk}%</p>
          <p className="text-[10px] uppercase tracking-wider text-rose-400/80 mt-0.5">ROI at Risk</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3 rounded-xl bg-rose-500/10 border border-rose-500/25 px-4 py-3">
        <span className="text-2xl font-extrabold tabular-nums text-rose-300">{Math.round(c.score)}</span>
        <div>
          <p className="text-sm font-semibold text-slate-100">{c.name}</p>
          <p className="text-[11px] text-rose-300/80">Constraint Pillar</p>
        </div>
      </div>
      {onSelectPillar && c.pillar_id && (
        <button
          onClick={() => onSelectPillar(c.pillar_id)}
          data-testid="maturity-primary-constraint-action"
          className="mt-4 w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors"
        >
          <Zap className="h-4 w-4" /> Take Action <ArrowRight className="h-4 w-4" />
        </button>
      )}
      <button
        onClick={() => setShowWhy((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#44b6d2] hover:text-cyan-300 transition-colors"
        data-testid="constraint-why-toggle"
      >
        Why is this a constraint? What does it mean?
        {showWhy ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {showWhy && <p className="mt-2 text-xs text-slate-300 leading-relaxed" data-testid="constraint-why-body">{TAB_INFO.primary_constraint.body}</p>}
      {symptoms.length > 0 && (
        <>
          <p className="mt-4 text-[10px] font-bold uppercase tracking-wider text-slate-500">Common symptoms</p>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {symptoms.map(([title, detail], i) => {
              const open = openSym === i;
              return (
                <div key={title} className="rounded-lg bg-[#0b1220] ring-1 ring-[#22304d]">
                  <button
                    onClick={() => setOpenSym(open ? null : i)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
                    data-testid={`constraint-symptom-${i}`}
                  >
                    <span className="text-xs font-medium text-slate-200">{title}</span>
                    <ChevronDown className={cn('h-3.5 w-3.5 text-slate-500 transition-transform shrink-0', open && 'rotate-180')} />
                  </button>
                  {open && <p className="px-3 pb-2.5 text-[11px] text-slate-400 leading-snug">{detail}</p>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </DarkCard>
  );
}

const RANGE_OPTIONS = [
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: '12m', label: '12 months', days: 365 },
  { id: 'all', label: 'All', days: null },
];

function filterHistoryByRange(history, days) {
  if (!days) return history || [];
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return (history || []).filter((h) => new Date(h.created_at).getTime() >= cutoff);
}

function MaturityAlertsStrip({ insights, threshold = 50, canEdit = false, onThresholdChange, navigate, financial }) {
  const leak = financial?.roi_leakage;
  const flagged = [...(insights.pillar_scores || [])]
    .filter((p) => p.score < threshold)
    .sort((a, b) => a.score - b.score);
  const cells = flagged.map((p) => ({
    label: p.name,
    sub: `Below ${threshold} threshold`,
    value: `${Math.round(p.score)}/100`,
    tone: p.score < threshold * 0.6 ? 'text-rose-300' : 'text-amber-300',
  }));
  if (leak?.range) {
    cells.push({ label: 'ROI Leakage', sub: `${leak.range} unrealized potential`, value: leak.mid_pct >= 15 ? 'High' : leak.mid_pct >= 8 ? 'Moderate' : 'Low', tone: 'text-amber-300' });
  }
  const allClear = flagged.length === 0;
  return (
    <DarkCard className="px-5 py-4" testId="maturity-recent-alerts">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className={cn('w-8 h-8 rounded-lg ring-1 flex items-center justify-center', allClear ? 'bg-emerald-500/15 ring-emerald-500/30' : 'bg-rose-500/15 ring-rose-500/30')}>
            {allClear ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300">Recent Alerts</p>
            <p className="text-xs text-slate-500">{allClear ? `All pillars are above the ${threshold} threshold.` : 'Your maturity gaps require attention.'}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/maturity-health/insights')}
          className="text-xs font-semibold text-[#44b6d2] hover:text-cyan-300 inline-flex items-center gap-1 transition-colors"
          data-testid="maturity-alerts-view-all"
        >
          View all alerts <ArrowRight className="h-3.5 w-3.5" />
        </button>
        <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-[260px]">
          {cells.slice(0, 4).map((cell, i) => (
            <div key={i} className="rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] px-3 py-2" data-testid={`maturity-alert-cell-${i}`}>
              <p className="text-[11px] font-semibold text-slate-200 truncate">{cell.label}</p>
              <p className="text-[10px] text-slate-500 truncate">{cell.sub}</p>
              <p className={cn('text-sm font-bold mt-0.5 tabular-nums', cell.tone)}>{cell.value}</p>
            </div>
          ))}
        </div>
        {cells.length > 4 && (
          <button
            onClick={() => navigate('/maturity-health/insights')}
            className="text-[11px] font-semibold text-slate-400 hover:text-slate-100 shrink-0 transition-colors"
            data-testid="maturity-alerts-more"
          >
            +{cells.length - 4} more
          </button>
        )}
        {canEdit ? (
          <span className="flex items-center gap-1.5 shrink-0 pl-3 border-l border-[#1f2a44]" data-testid="maturity-alert-threshold-control">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Flag below</span>
            <button onClick={() => onThresholdChange?.(Math.max(0, threshold - 5))} className="h-6 w-6 rounded-md bg-[#0b1220] ring-1 ring-[#22304d] text-slate-300 hover:text-white flex items-center justify-center" data-testid="maturity-alert-threshold-down" aria-label="Lower threshold"><Minus className="h-3 w-3" /></button>
            <span className="text-sm font-bold text-slate-100 tabular-nums w-8 text-center" data-testid="maturity-alert-threshold-value">{threshold}</span>
            <button onClick={() => onThresholdChange?.(Math.min(100, threshold + 5))} className="h-6 w-6 rounded-md bg-[#0b1220] ring-1 ring-[#22304d] text-slate-300 hover:text-white flex items-center justify-center" data-testid="maturity-alert-threshold-up" aria-label="Raise threshold"><Plus className="h-3 w-3" /></button>
          </span>
        ) : (
          <span className="text-[10px] text-slate-500 shrink-0 pl-3 border-l border-[#1f2a44]" data-testid="maturity-alert-threshold-readonly">Flag below {threshold}</span>
        )}
      </div>
    </DarkCard>
  );
}

function MaturityOverview({ insights, financial, benchmark, history, leiQuarterDelta, onSelectPillar, navigate, threshold, canEdit, onThresholdChange }) {
  const { primary_constraint } = insights;
  const [range, setRange] = useState('12m');
  const rangeDef = RANGE_OPTIONS.find((r) => r.id === range) || RANGE_OPTIONS[2];
  const trendHistory = filterHistoryByRange(history, rangeDef.days);
  const hasEnoughOverall = (history || []).length >= 2;
  const maturityFocus = primary_constraint?.pillar_id ? {
    engine: 'maturity',
    entityId: primary_constraint.pillar_id,
    entityLabel: primary_constraint.name,
    pillarId: primary_constraint.pillar_id,
    score: Math.round(primary_constraint.score),
    scoreLabel: `${Math.round(primary_constraint.score)} / 100`,
    target: 100,
    gapPct: Math.max(0, 100 - Math.round(primary_constraint.score)),
    lift: 10,
    overallScore: Math.round(insights.overall_score || 0),
    granularity: 'pillar',
    granularityLabel: 'pillar',
  } : null;
  return (
    <div className="space-y-4" data-testid="maturity-overview">
      <MaturityKpiTiles insights={insights} financial={financial} leiQuarterDelta={leiQuarterDelta} onSelectPillar={onSelectPillar} />
      {maturityFocus && <WeakestMeasureTile focus={maturityFocus} onView={() => onSelectPillar(primary_constraint.pillar_id)} testId="maturity-weakest-measure-tile" />}
      <MaturityAlertsStrip
        insights={insights}
        financial={financial}
        navigate={navigate}
        threshold={threshold}
        canEdit={canEdit}
        onThresholdChange={onThresholdChange}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7">
          <PillarBreakdownCard
            pillarScores={insights.pillar_scores}
            constraintId={primary_constraint?.pillar_id}
            onSelect={onSelectPillar}
          />
        </div>
        <div className="lg:col-span-5">
          <PrimaryConstraintCard insights={insights} onSelectPillar={onSelectPillar} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <DarkCard className="p-5 lg:col-span-7" testId="maturity-trend">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
              <TrendingUp className="h-4 w-4 text-[#44b6d2]" /> Maturity Trend
              <TileInfoButton testid="maturity-trend-info" title={TAB_INFO.trend.title} body={TAB_INFO.trend.body} />
            </div>
            <div className="inline-flex items-center rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] overflow-hidden" data-testid="maturity-trend-range">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setRange(r.id)}
                  className={cn(
                    'px-2.5 py-1 text-[11px] font-semibold transition-colors',
                    range === r.id ? 'bg-[#44b6d2] text-[#04222b]' : 'text-slate-400 hover:text-slate-100',
                  )}
                  data-testid={`maturity-trend-range-${r.id}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {trendHistory.length >= 2 ? (
            <MaturityTrendChart history={trendHistory} benchmark={benchmark} />
          ) : hasEnoughOverall ? (
            <p className="text-sm text-slate-500 py-10 text-center" data-testid="maturity-trend-empty-window">
              {trendHistory.length === 1
                ? `Only one assessment in the selected ${rangeDef.label.toLowerCase()} window — widen the range to see a trend.`
                : `No assessments in the selected ${rangeDef.label.toLowerCase()} window. Widen the range to see your trend.`}
            </p>
          ) : (
            <p className="text-sm text-slate-500 py-10 text-center">Run a second assessment to unlock your maturity trend over time.</p>
          )}
        </DarkCard>
        <div className="lg:col-span-5" data-testid="maturity-overview-action-plan">
          {primary_constraint?.pillar_id
            ? <MaturityActionPlanCard pillarId={primary_constraint.pillar_id} pillarName={primary_constraint.name} />
            : null}
        </div>
      </div>
    </div>
  );
}


function PillarCompareColumn({ ps, benchmark, onOpen }) {
  const color = getBarColor(ps.score);
  const band = getBand(ps.score);
  const Icon = PILLAR_ICONS[ps.pillar_id] || Activity;
  const bench = (benchmark?.pillar_comparison || []).find((p) => p.pillar_id === ps.pillar_id);
  const fin = PILLAR_FINANCIAL_NARRATIVES[ps.pillar_id];
  return (
    <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] p-4" data-testid={`compare-col-${ps.pillar_id}`}>
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-[#111827] ring-1 ring-[#22304d] flex items-center justify-center">
          <Icon className="h-4 w-4 text-[#44b6d2]" />
        </span>
        <p className="text-sm font-semibold text-slate-100 leading-tight">{ps.name}</p>
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-extrabold tabular-nums mc-band" style={{ color, '--bl': getBarColorLight(ps.score) }}>{Math.round(ps.score)}</span>
        <span className="text-xs text-slate-500">/100 · {band}</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-[#111827] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${ps.score}%`, backgroundColor: color }} />
      </div>
      <dl className="mt-3 space-y-1.5 text-xs">
        <div className="flex justify-between"><dt className="text-slate-500">Weight</dt><dd className="text-slate-300 font-semibold tabular-nums">{Math.round((ps.weight || 0) * 100)}%</dd></div>
        {bench && (
          <>
            <div className="flex justify-between"><dt className="text-slate-500">Industry avg</dt><dd className="text-slate-300 font-semibold tabular-nums">{Math.round(bench.avg_score)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">vs peers</dt><dd className={cn('font-semibold tabular-nums', bench.delta >= 0 ? 'text-emerald-300' : 'text-rose-300')}>{bench.delta > 0 ? '+' : ''}{Math.round(bench.delta)} pts</dd></div>
          </>
        )}
      </dl>
      {fin?.actions?.[0] && (
        <p className="mt-3 text-[11px] text-slate-400 leading-snug"><span className="font-semibold text-slate-300">Top move: </span>{fin.actions[0]}</p>
      )}
      <button onClick={() => onOpen(ps.pillar_id)} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#44b6d2] hover:text-cyan-300" data-testid={`compare-open-${ps.pillar_id}`}>
        Open full deep dive <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function MaturityPillarCompare({ pillarScores, benchmark, onOpen, canExport = false }) {
  const sorted = [...pillarScores].sort((a, b) => a.score - b.score);
  const [aId, setAId] = useState(sorted[0]?.pillar_id);
  const [bId, setBId] = useState(sorted[1]?.pillar_id);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  const a = pillarScores.find((p) => p.pillar_id === aId);
  const b = pillarScores.find((p) => p.pillar_id === bId);
  const diff = a && b ? Math.round(a.score - b.score) : 0;

  const handleExport = async () => {
    if (!exportRef.current || !a || !b) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, jspdfMod] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      const jsPDF = jspdfMod.jsPDF || jspdfMod.default;
      const canvas = await html2canvas(exportRef.current, { backgroundColor: '#0a0f1c', scale: 2 });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      pdf.setFillColor(10, 15, 28);
      pdf.rect(0, 0, pw, ph, 'F');
      pdf.setTextColor(226, 232, 240);
      pdf.setFontSize(16);
      pdf.text('Maturity Pillar Comparison', 40, 44);
      pdf.setFontSize(11);
      pdf.setTextColor(148, 163, 184);
      pdf.text(`${a.name}  vs  ${b.name}`, 40, 62);
      pdf.text(new Date().toLocaleDateString(), pw - 40, 44, { align: 'right' });
      const iw = pw - 80;
      const ih = (canvas.height / canvas.width) * iw;
      pdf.addImage(img, 'PNG', 40, 80, iw, Math.min(ih, ph - 110));
      pdf.save(`pillar-compare-${a.pillar_id}-vs-${b.pillar_id}.pdf`);
      toast.success('Comparison exported to PDF');
    } catch (e) {
      toast.error('Could not export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <DarkCard className="p-5 mt-5" testId="maturity-pillar-compare">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
          <BarChart3 className="h-4 w-4 text-[#44b6d2]" /> Compare two pillars
        </div>
        {canExport && a && b && (
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] px-3 py-1.5 text-xs font-semibold text-slate-200 hover:text-white hover:ring-[#44b6d2]/50 transition-colors disabled:opacity-50"
            data-testid="compare-export-pdf"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-4">Pin two pillars side-by-side to compare gaps, benchmark position, and their top action.</p>
      <div className="grid grid-cols-2 gap-3 mb-4 max-w-xl">
        <Select value={aId} onValueChange={setAId}>
          <SelectTrigger className="bg-[#0b1220] border-[#22304d] text-slate-100" data-testid="compare-select-a"><SelectValue /></SelectTrigger>
          <SelectContent>
            {pillarScores.map((p) => <SelectItem key={p.pillar_id} value={p.pillar_id} disabled={p.pillar_id === bId}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={bId} onValueChange={setBId}>
          <SelectTrigger className="bg-[#0b1220] border-[#22304d] text-slate-100" data-testid="compare-select-b"><SelectValue /></SelectTrigger>
          <SelectContent>
            {pillarScores.map((p) => <SelectItem key={p.pillar_id} value={p.pillar_id} disabled={p.pillar_id === aId}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {a && b && (
        <div ref={exportRef}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PillarCompareColumn ps={a} benchmark={benchmark} onOpen={onOpen} />
            <PillarCompareColumn ps={b} benchmark={benchmark} onOpen={onOpen} />
          </div>
          <p className="mt-3 text-xs text-slate-400" data-testid="compare-gap">
            <span className="font-semibold text-slate-200">{a.name}</span> is{' '}
            <span className={cn('font-bold', diff === 0 ? 'text-slate-300' : diff > 0 ? 'text-emerald-300' : 'text-rose-300')}>
              {diff === 0 ? 'level with' : `${Math.abs(diff)} pts ${diff > 0 ? 'ahead of' : 'behind'}`}
            </span>{' '}
            <span className="font-semibold text-slate-200">{b.name}</span>.
          </p>
        </div>
      )}
    </DarkCard>
  );
}

function MaturityDeepDivePicker({ pillarScores, constraintId, benchmark, onSelect, canExport }) {
  return (
    <div data-testid="maturity-deep-dives-picker">
      <p className="text-sm text-slate-400 mb-4">Select a pillar to explore its full breakdown, benchmark position, and action plan.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...pillarScores].sort((a, b) => b.score - a.score).map((ps) => {
          const Icon = PILLAR_ICONS[ps.pillar_id] || Activity;
          const color = getBarColor(ps.score);
          const band = getBand(ps.score);
          return (
            <button
              key={ps.pillar_id}
              onClick={() => onSelect(ps.pillar_id)}
              className="text-left rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] hover:ring-[#44b6d2]/50 p-5 shadow-xl transition-all"
              data-testid={`deep-dive-card-${ps.pillar_id}`}
            >
              <div className="flex items-center justify-between">
                <span className="w-10 h-10 rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center">
                  <Icon className="h-5 w-5 text-[#44b6d2]" />
                </span>
                {ps.pillar_id === constraintId && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Constraint</span>
                )}
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-100">{ps.name}</p>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-2xl font-extrabold tabular-nums mc-band" style={{ color, '--bl': getBarColorLight(ps.score) }}>{Math.round(ps.score)}</span>
                <span className="text-xs text-slate-500">/100 · {band}</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-[#0b1220] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${ps.score}%`, backgroundColor: color }} />
              </div>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#44b6d2]">
                Open deep dive <ChevronRight className="h-3.5 w-3.5" />
              </span>
            </button>
          );
        })}
      </div>
      <MaturityPillarCompare pillarScores={pillarScores} benchmark={benchmark} onOpen={onSelect} canExport={canExport} />
    </div>
  );
}

function MaturityDeepDive({ pillarId, insights, benchmark, navigate, onSelectPillar }) {
  const location = useLocation();
  const ps = insights.pillar_scores.find((p) => p.pillar_id === pillarId);
  // Deep-link arrival pulse — when a Navigation Health "Top Maturity Drivers"
  // click lands here (state.fromDriver), briefly scroll to + glow the pillar
  // header so the user sees exactly what they landed on. Manual pillar
  // switches (chips) carry no such state, so they don't pulse.
  useEffect(() => {
    if (!location.state?.fromDriver) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="maturity-deep-dive"] .mc-deep-dive-header');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('deep-link-pulse');
      setTimeout(() => el.classList.remove('deep-link-pulse'), 2800);
    }, 250);
    return () => clearTimeout(t);
  }, [location.state, pillarId]);
  if (!ps) {
    return (
      <div className="text-sm text-slate-400" data-testid="maturity-deep-dive-missing">
        Pillar not found.{' '}
        <button className="text-[#44b6d2] font-medium" onClick={() => navigate('/maturity-health/deep-dives')}>Back to pillars</button>
      </div>
    );
  }
  const Icon = PILLAR_ICONS[ps.pillar_id] || Activity;
  const color = getBarColor(ps.score);
  const band = getBand(ps.score);
  const narr = PILLAR_NARRATIVES[ps.pillar_id];
  const narrText = narr ? (ps.score >= 65 ? narr.high : ps.score >= 35 ? narr.mid : narr.low) : '';
  const fin = PILLAR_FINANCIAL_NARRATIVES[ps.pillar_id];
  const bench = (benchmark?.pillar_comparison || []).find((p) => p.pillar_id === pillarId);
  const isConstraint = insights.primary_constraint?.pillar_id === pillarId;
  return (
    <div className="space-y-4" data-testid="maturity-deep-dive">
      {/* Breadcrumb + pillar switcher */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => navigate('/maturity-health/deep-dives')}
          className="text-xs text-slate-400 hover:text-slate-100 inline-flex items-center gap-1 transition-colors"
          data-testid="deep-dive-back"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> All pillars
        </button>
        <span className="text-slate-700">·</span>
        {insights.pillar_scores.map((p) => (
          <button
            key={p.pillar_id}
            onClick={() => onSelectPillar(p.pillar_id)}
            className={cn(
              'text-[11px] px-2.5 py-1 rounded-full transition-colors',
              p.pillar_id === pillarId ? 'bg-[#44b6d2] text-[#04222b] font-semibold' : 'text-slate-400 hover:bg-white/5',
            )}
            data-testid={`deep-dive-switch-${p.pillar_id}`}
          >
            {pillarShort(p.name)}
          </button>
        ))}
      </div>

      {/* Header */}
      <DarkCard className={cn('p-5 mc-deep-dive-header', isConstraint && '!ring-rose-500/25')}>
        <div className="flex items-start gap-4 flex-wrap">
          <span className="w-12 h-12 rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center shrink-0">
            <Icon className="h-6 w-6 text-[#44b6d2]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-100">{ps.name}</h2>
              {isConstraint && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Primary Constraint</span>
              )}
              <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', bandChipClasses(ps.score))}>{band}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Pillar weight in overall maturity: {Math.round((ps.weight || 0) * 100)}%</p>
            <div className="mt-3 h-2 rounded-full bg-[#0b1220] overflow-hidden max-w-md">
              <div className="h-full rounded-full" style={{ width: `${ps.score}%`, backgroundColor: color }} />
            </div>
            {bench && (
              <p className="text-xs text-slate-400 mt-2 tabular-nums">
                You: <span className="font-semibold mc-band" style={{ color, '--bl': getBarColorLight(ps.score) }}>{Math.round(bench.your_score)}</span>
                {'  ·  '}Industry avg: <span className="font-semibold text-slate-300">{Math.round(bench.avg_score)}</span>
                {'  ·  '}
                <span className={cn('font-semibold', bench.delta >= 0 ? 'text-emerald-300' : 'text-rose-300')}>
                  {bench.delta > 0 ? '+' : ''}{Math.round(bench.delta)} pts vs peers
                </span>
              </p>
            )}
          </div>
          <ScoreGauge score={ps.score} size={88} />
        </div>
      </DarkCard>

      {/* Narrative + financial impact */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <DarkCard className="p-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#44b6d2] mb-2">Impact on your value chain</p>
          <p className="text-sm text-slate-300 leading-relaxed">{narrText}</p>
        </DarkCard>
        {fin && (
          <DarkCard className="p-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-300 mb-2">Financial impact</p>
            <p className="text-sm text-slate-300 leading-relaxed">{fin.impact}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-4 mb-2">Recommended actions</p>
            <ul className="space-y-2">
              {(fin.actions || []).map((a, i) => (
                <li key={i} className="flex gap-2 text-xs text-slate-300">
                  <span className="shrink-0 w-4 h-4 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <span className="leading-snug">{a}</span>
                </li>
              ))}
            </ul>
          </DarkCard>
        )}
      </div>

      {/* Action plan (AI + static) for this pillar */}
      <MaturityActionPlanCard pillarId={pillarId} pillarName={ps.name} />
    </div>
  );
}


export default function MaturityHealthPage() {
  // Maturity data is scoped server-side via the X-View-As-Org header, so a
  // ?organization_id= deep link works by aligning the global preview org
  // (the hook does this for super admins). loadData re-runs when that org
  // changes so the page refetches for the linked tenant.
  const { viewAsOrg, isSuperAdmin, isOrgAdmin, userRole } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  useDeepLinkOrg();
  const location = useLocation();
  const navigate = useNavigate();
  const segs = location.pathname.split('/').filter(Boolean);
  const subView = segs[1] || 'overview';
  const pillarId = subView === 'deep-dives' ? (segs[2] || null) : null;
  const activeTab = subView === 'report-card' ? 'report' : (MATURITY_TAB_SEGS.includes(subView) ? subView : 'insights');
  const viewAsOrgId = viewAsOrg?.id || null;
  const [insights, setInsights] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [roadmap, setRoadmap] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roadmapLoading, setRoadmapLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [maturityThreshold, setMaturityThreshold] = useState(50);
  const canEditThreshold = isOrgAdmin || isSuperAdmin;

  // Cross-app quarter delta for the headline LEI score —
  // surfaces "vs last quarter" chip on the hero tile and feeds
  // the pin payload so it carries into the CFO PDF.
  const leiQuarterDelta = useQuarterDelta('lei');

  // Org-scoped Maturity pillar-flag threshold (drives Recent Alerts).
  useEffect(() => {
    const q = viewAsOrgId ? `?organization_id=${viewAsOrgId}` : '';
    api.get(`/modelling-settings/maturity-alert-threshold${q}`)
      .then((res) => {
        if (typeof res.data?.maturity_alert_threshold === 'number') {
          setMaturityThreshold(res.data.maturity_alert_threshold);
        }
      })
      .catch(() => {});
  }, [viewAsOrgId]);

  const thresholdSaveTimer = useRef(null);
  const handleThresholdChange = (next) => {
    const val = Math.max(0, Math.min(100, next));
    setMaturityThreshold(val);
    if (thresholdSaveTimer.current) clearTimeout(thresholdSaveTimer.current);
    thresholdSaveTimer.current = setTimeout(() => {
      api.post('/modelling-settings/maturity-alert-threshold', {
        maturity_alert_threshold: val,
        ...(viewAsOrgId ? { organization_id: viewAsOrgId } : {}),
      }).then(() => toast.success(`Alerts now flag pillars below ${val}`))
        .catch(() => toast.error('Could not save threshold'));
    }, 500);
  };

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/maturity-assessment/insights'),
      api.get('/maturity-assessment/benchmark'),
      api.get('/maturity-assessment/financial'),
      api.get('/maturity-assessment/history'),
    ]).then(([insRes, benchRes, finRes, histRes]) => {
      setInsights(insRes.data);
      setBenchmark(benchRes.data);
      setFinancial(finRes.data);
      setHistory(histRes.data.assessments || []);
    }).catch(err => logger.error('Error loading maturity health:', err))
      .finally(() => setLoading(false));
    // viewAsOrgId is intentionally a trigger: data is scoped via the
    // X-View-As-Org header, so changing org must re-run this fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsOrgId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Deep-link from the home leak banner's "fix the root cause" CTA:
  // /maturity-health?focus=<pillarId> scrolls to + briefly highlights
  // the Priority Focus (3-step plan) section once data has loaded.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!insights || !params.get('focus')) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="maturity-priority-focus-section"]');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-rose-400', 'ring-offset-2', 'rounded-xl');
      setTimeout(() => el.classList.remove('ring-2', 'ring-rose-400', 'ring-offset-2', 'rounded-xl'), 2800);
    }, 700);
    return () => clearTimeout(t);
  }, [insights]);

  // Driver deep-link from Navigation Health's "Top Maturity Drivers":
  // /maturity-health?pillar=<pillar name> resolves the name to its pillar
  // id and redirects into that pillar's Deep Dive so the click lands on the
  // detailed breakdown rather than the generic overview.
  useEffect(() => {
    if (!insights?.pillar_scores?.length) return;
    const wanted = new URLSearchParams(location.search).get('pillar');
    if (!wanted) return;
    const norm = (s) => (s || '').trim().toLowerCase();
    const w = norm(wanted);
    const match = insights.pillar_scores.find(p => norm(p.name) === w)
      || insights.pillar_scores.find(p => norm(p.name).includes(w) || w.includes(norm(p.name)));
    if (match?.pillar_id) {
      navigate(`/maturity-health/deep-dives/${match.pillar_id}`, { replace: true, state: { fromDriver: true } });
    } else {
      // Unmatched pillar name — drop the stale query so the overview URL is clean.
      navigate('/maturity-health', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insights, location.search]);

  useEffect(() => {
    if (activeTab !== 'roadmap' || roadmap !== undefined) return;
    api.get('/maturity-assessment/roadmap').then((r) => setRoadmap(r.data)).catch(() => setRoadmap(null));
  }, [activeTab, roadmap]);
  const [roadmapOwners, setRoadmapOwners] = useState([]);
  const canAssignRoadmap = isSuperAdmin || ['org_admin', 'manager', 'team_lead', 'hr_admin', 'l_and_d'].includes(userRole);
  useEffect(() => {
    if (activeTab !== 'roadmap' || !canAssignRoadmap || roadmapOwners.length) return;
    api.get('/maturity-assessment/roadmap/owners').then((r) => setRoadmapOwners(r.data.owners || [])).catch(() => {});
  }, [activeTab, canAssignRoadmap, roadmapOwners.length]);
  const handleAssignAction = async (phase, index, { owner_id, due_date }) => {
    try {
      const r = await api.put('/maturity-assessment/roadmap/assign', { phase, index, owner_id, due_date });
      setRoadmap(r.data);
      toast.success(owner_id ? 'Owner assigned — they have been notified' : 'Assignment cleared');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not assign the action');
      throw err;
    }
  };
  const handleToggleAction = async (phase, index, done) => {
    try {
      const r = await api.put('/maturity-assessment/roadmap/progress', { phase, index, done });
      setRoadmap(r.data);
      if (done) toast.success('Marked done — the next refresh will build on it');
    } catch (err) {
      logger.error('Roadmap progress failed:', err);
      toast.error('Could not update the action');
    }
  };
  const handleGenerateRoadmap = async (refresh = false) => {
    setRoadmapLoading(true);
    try {
      const res = await api.post('/maturity-assessment/roadmap', { refresh: !!refresh });
      setRoadmap(res.data);
      toast.success(refresh ? `Roadmap refreshed${res.data.completed_actions?.length ? ` — built on ${res.data.completed_actions.length} completed action${res.data.completed_actions.length > 1 ? 's' : ''}` : ''}` : 'Roadmap generated');
    } catch (err) {
      logger.error('Roadmap generation failed:', err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : err?.code === 'ECONNABORTED' ? 'The AI took too long to respond — please try again' : 'Failed to generate roadmap', { duration: 8000 });
    } finally {
      setRoadmapLoading(false);
    }
  };

  const handleAssessmentComplete = () => {
    loadData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const hasAssessment = insights?.has_assessment;

  if (!hasAssessment) {
    return (
      <div className="p-6 text-center py-20" data-testid="maturity-health-page">
        <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-slate-800 mb-2">No Maturity Assessment Found</h2>
        <p className="text-sm text-slate-500 mb-6">Complete the 30-question diagnostic to see your organizational learning maturity health, insights, and improvement roadmap.</p>
        <Button onClick={() => setShowOverlay(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="take-assessment-cta">
          <ClipboardCheck className="h-4 w-4 mr-2" /> Take Assessment
        </Button>
        {showOverlay && (
          <MaturityAssessmentOverlay
            onClose={() => setShowOverlay(false)}
            onComplete={handleAssessmentComplete}
          />
        )}
      </div>
    );
  }

  const { overall_score, maturity_band, uplift, primary_constraint } = insights;
  const theme = healthHeroTheme(isDark, getSectionTheme, getLightSectionTheme);
  const selectPillar = (pid) => navigate(`/maturity-health/deep-dives/${pid}`);
  const isTabSeg = MATURITY_TAB_SEGS.includes(subView);

  const heroActions = (
    <div className="flex items-center gap-2">
      <PageToolbar theme={theme} organizationId={viewAsOrgId} testId="maturity-toolbar" />
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 bg-[#111827] border-[#1f2a44] text-slate-200 hover:bg-[#1a2438] hover:text-white"
        onClick={() => setShowOverlay(true)}
        data-testid="retake-assessment-btn"
      >
        <ClipboardCheck className="h-4 w-4" /> {hasAssessment ? 'Retake' : 'Take Assessment'}
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 bg-[#111827] border-[#1f2a44] text-slate-200 hover:bg-[#1a2438] hover:text-white"
        onClick={() => window.location.assign('/maturity-reference')}
        data-testid="maturity-health-ref-link"
      >
        <FileText className="h-4 w-4" /> Reference
      </Button>
    </div>
  );

  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Maturity Dashboard"
          description="Your maturity score reflects how effectively your organization's learning ecosystem converts investment into measurable business outcomes."
          theme={theme}
          icon={Gauge}
          actions={heroActions}
          pinSnapshot={[
            {
              label: 'Maturity Score',
              value: `${Math.round(overall_score)}/100`,
              hint: maturity_band ? `${maturity_band} Maturity` : undefined,
              ...(leiQuarterDelta ? {
                delta: leiQuarterDelta.delta,
                deltaText: leiQuarterDelta.deltaText,
                deltaLabel: leiQuarterDelta.deltaLabel,
              } : {}),
            },
            { label: 'Primary Constraint', value: primary_constraint ? (SHORT_NAMES[primary_constraint.name] || primary_constraint.name) : '—' },
            { label: 'Uplift Potential', value: `+${uplift?.roi_improvement_pct ?? 0}% ROI`, hint: uplift?.next_band ? `When advancing to ${uplift.next_band}` : undefined },
          ]}
          testId="maturity-hero"
        >
          <HeroAccents />
        </PageHero>
      )}
    >
      <div className={cn('space-y-6', isDark ? 'home-dark-scope' : 'mc-light')} data-testid="maturity-health-page">
      <PageSubNav items={MATURITY_SUBNAV} />

      {subView === 'overview' && (
        <MaturityOverview
          insights={insights}
          financial={financial}
          benchmark={benchmark}
          history={history}
          leiQuarterDelta={leiQuarterDelta}
          onSelectPillar={selectPillar}
          navigate={navigate}
          threshold={maturityThreshold}
          canEdit={canEditThreshold}
          onThresholdChange={handleThresholdChange}
        />
      )}

      {subView === 'deep-dives' && (
        pillarId ? (
          <MaturityDeepDive
            pillarId={pillarId}
            insights={insights}
            benchmark={benchmark}
            navigate={navigate}
            onSelectPillar={selectPillar}
          />
        ) : (
          <MaturityDeepDivePicker
            pillarScores={insights.pillar_scores}
            constraintId={primary_constraint?.pillar_id}
            benchmark={benchmark}
            onSelect={selectPillar}
            canExport={canEditThreshold}
          />
        )
      )}

      {isTabSeg && (
        <div data-testid="maturity-tab-view">
          <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
            {(TAB_INFO[activeTab] || {}).title}
            <TileInfoButton
              testid={`tab-info-${activeTab}`}
              title={(TAB_INFO[activeTab] || {}).title}
              body={(TAB_INFO[activeTab] || {}).body}
            />
          </div>
          {activeTab === 'insights' && <InsightsTab data={insights} />}
          {activeTab === 'roadmap' && <RoadmapTab data={roadmap} onGenerate={handleGenerateRoadmap} loading={roadmapLoading} onToggleAction={handleToggleAction} onAssign={handleAssignAction} owners={roadmapOwners} canAssign={canAssignRoadmap} />}
          {activeTab === 'benchmark' && <BenchmarkTab initialData={benchmark} />}
          {activeTab === 'analysis' && <AnalysisTab pillarScores={insights.pillar_scores} />}
          {activeTab === 'financial' && <FinancialTab data={financial} />}
          {activeTab === 'history' && <HistoryTab history={history} />}
          {activeTab === 'report' && <ReportCardTab />}
        </div>
      )}

      {/* Assessment Overlay */}
      {showOverlay && (
        <MaturityAssessmentOverlay
          onClose={() => setShowOverlay(false)}
          onComplete={handleAssessmentComplete}
        />
      )}
      </div>
    </PageShell>
  );
}
